import { publicAssetUrl } from "@/lib/public-image-url";
// Task #6522 — country-scoped SEO hire landing pages.
//
// URL family (market-relative, rendered under the /{country} router base):
//   /hire/{makeSlug}                → pathType "make"
//   /hire/{makeSlug}/{modelSlug}    → pathType "model"
//   /wedding-car-hire               → pathType "wedding"
//
// Unlike the existing geo guides (worldwide pages that hreflang across every
// market), each entry here targets ONE country. Canonicals point at the single
// country URL and hreflang is emitted only between locale variants that
// actually exist in this registry (localeGroup ties them together).
//
// Copy is embedded in the entry — including the Danish (/dk) and Italian
// (/it) pages — deliberately bypassing the i18n bundles: these are one-off
// editorial pages, and adding keys to en.json would trip the 8-locale strict
// translation gate for content that must NOT be machine-translated to the
// other six locales. Adding a new page = adding one entry (plus its mirror
// row in api-server/src/content/geo-slugs.ts for the sitemap).
//
// Inventory figures referenced in copy were pulled from live prod data on
// 2026-07-17 (counts, chauffeur-from rates, cities). The page itself always
// renders the LIVE price-from + counts from the search API; copy avoids
// hard numbers that would rot, and references ranges/cities instead.

import type { ContentSection, FaqItem, GeoListingsQuery } from "./types";

export type HireLandingPathType = "make" | "model" | "wedding";

export interface HireLanding {
  /** URL market this page belongs to ("uk", "dk", "ca", "it", "no", "se"). */
  market: string;
  pathType: HireLandingPathType;
  /** e.g. "ford" — required for make/model pages. */
  makeSlug?: string;
  /** e.g. "mustang" — required for model pages. */
  modelSlug?: string;
  /**
   * Task #7156 — optional custom single-segment slug for wedding pages in
   * the market's native language (e.g. "bryllupsbil", "hochzeitsauto-mieten").
   * Must be ASCII slug-safe (lowercase a-z, 0-9, hyphens); accented native
   * words are transliterated ("bröllopsbil" → "brollopsbil"). When absent,
   * wedding pages live at "/wedding-car-hire".
   */
  slug?: string;
  /** Content language of this entry ("en", "da", "it", …). */
  locale: string;
  /**
   * Ties locale variants of the SAME page together for hreflang. Pages with
   * a unique group (the common case at launch) emit only themselves +
   * x-default. Absent = derived from market+path (no variants).
   */
  localeGroup?: string;
  /** Reviewed primary for an existing direct-200 alias, not stock identity. */
  canonicalPath?: string;
  h1: string;
  metaTitle: string;
  metaDescription: string;
  /** 2–3 sentence hero intro under the H1. */
  heroIntro: string;
  heroImage: string;
  heroAlt: string;
  /** 400–600 words of unique editorial. */
  sections: ContentSection[];
  faqs: FaqItem[];
  listingsQuery: GeoListingsQuery;
  /** Market-relative internal links (3+). */
  related: { label: string; href: string; searchState?: GeoListingsQuery }[];
  /** Localised UI strings for the non-i18n-bundle chrome on this page. */
  ui: {
    listingsHeading: string;
    viewAllCta: string;
    priceFromLabel: string;
    breadcrumbHome: string;
    breadcrumbHire: string;
    faqHeading: string;
    relatedHeading: string;
    fallbackHeading: string;
    fallbackBody: string;
    fallbackEmailPlaceholder: string;
    fallbackEmailCta: string;
    fallbackEmailSuccess: string;
    chauffeuredLabel: string;
    selfDriveLabel: string;
    perHour: string;
    perDay: string;
  };
  updatedAt: string;
}

/** Market-relative path for an entry ("/hire/ford/mustang" etc.). */
export function hireLandingPath(e: HireLanding): string {
  if (e.pathType === "wedding") return e.slug ? `/${e.slug}` : "/wedding-car-hire";
  return e.modelSlug ? `/hire/${e.makeSlug}/${e.modelSlug}` : `/hire/${e.makeSlug}`;
}

const EN_UI: HireLanding["ui"] = {
  listingsHeading: "Live listings",
  viewAllCta: "Rent a car ({n})",
  priceFromLabel: "from",
  breadcrumbHome: "Home",
  breadcrumbHire: "Hire",
  faqHeading: "Frequently asked questions",
  relatedHeading: "Keep exploring",
  fallbackHeading: "Nearby cars you can book today",
  fallbackBody:
    "We don't have many exact matches live right now, so here are close alternatives from nearby. Leave your email and we'll let you know the moment a matching car lists.",
  fallbackEmailPlaceholder: "you@example.com",
  fallbackEmailCta: "Notify me",
  fallbackEmailSuccess: "Thanks! We'll email you when a matching car lists.",
  chauffeuredLabel: "Chauffeur",
  selfDriveLabel: "Self-drive",
  perHour: "/hour",
  perDay: "/day",
};

const DA_UI: HireLanding["ui"] = {
  listingsHeading: "Biler til leje lige nu",
  viewAllCta: "Lej en bil ({n})",
  priceFromLabel: "fra",
  breadcrumbHome: "Forside",
  breadcrumbHire: "Lej",
  faqHeading: "Ofte stillede spørgsmål",
  relatedHeading: "Se også",
  fallbackHeading: "Biler i nærheden, du kan booke i dag",
  fallbackBody:
    "Vi har ikke mange præcise match lige nu, så her er gode alternativer i nærheden. Skriv din e-mail, så giver vi besked, når en matchende bil bliver oprettet.",
  fallbackEmailPlaceholder: "dig@eksempel.dk",
  fallbackEmailCta: "Giv mig besked",
  fallbackEmailSuccess: "Tak! Vi skriver, når en matchende bil er klar.",
  chauffeuredLabel: "Med chauffør",
  selfDriveLabel: "Kør selv",
  perHour: "/time",
  perDay: "/dag",
};

const IT_UI: HireLanding["ui"] = {
  listingsHeading: "Auto disponibili ora",
  viewAllCta: "Noleggia un'auto ({n})",
  priceFromLabel: "da",
  breadcrumbHome: "Home",
  breadcrumbHire: "Noleggio",
  faqHeading: "Domande frequenti",
  relatedHeading: "Continua a esplorare",
  fallbackHeading: "Auto nelle vicinanze prenotabili oggi",
  fallbackBody:
    "Al momento non abbiamo molte corrispondenze esatte: ecco alcune alternative vicine. Lascia la tua email e ti avvisiamo appena un'auto corrispondente viene pubblicata.",
  fallbackEmailPlaceholder: "tu@esempio.it",
  fallbackEmailCta: "Avvisami",
  fallbackEmailSuccess: "Grazie! Ti scriveremo appena un'auto corrispondente sarà disponibile.",
  chauffeuredLabel: "Con autista",
  selfDriveLabel: "Guida tu",
  perHour: "/ora",
  perDay: "/giorno",
};

const SV_UI: HireLanding["ui"] = {
  listingsHeading: "Bilar att hyra just nu",
  viewAllCta: "Hyr en bil ({n})",
  priceFromLabel: "från",
  breadcrumbHome: "Hem",
  breadcrumbHire: "Hyr",
  faqHeading: "Vanliga frågor",
  relatedHeading: "Utforska vidare",
  fallbackHeading: "Bilar i närheten som kan bokas i dag",
  fallbackBody:
    "Vi har inte många exakta träffar just nu, så här är närliggande alternativ. Lämna din e-post så hör vi av oss så snart en matchande bil publiceras.",
  fallbackEmailPlaceholder: "du@exempel.se",
  fallbackEmailCta: "Meddela mig",
  fallbackEmailSuccess: "Tack! Vi hör av oss när en matchande bil finns.",
  chauffeuredLabel: "Med chaufför",
  selfDriveLabel: "Kör själv",
  perHour: "/timme",
  perDay: "/dag",
};

const DE_UI: HireLanding["ui"] = {
  listingsHeading: "Jetzt verfügbare Fahrzeuge",
  viewAllCta: "Auto mieten ({n})",
  priceFromLabel: "ab",
  breadcrumbHome: "Startseite",
  breadcrumbHire: "Mieten",
  faqHeading: "Häufige Fragen",
  relatedHeading: "Weiter stöbern",
  fallbackHeading: "Fahrzeuge in der Nähe, heute buchbar",
  fallbackBody:
    "Aktuell gibt es wenige exakte Treffer. Hier sind passende Alternativen aus der Umgebung. Hinterlassen Sie Ihre E-Mail und wir melden uns, sobald ein passendes Fahrzeug eingestellt wird.",
  fallbackEmailPlaceholder: "sie@beispiel.de",
  fallbackEmailCta: "Benachrichtigen",
  fallbackEmailSuccess: "Danke! Wir melden uns, sobald ein passendes Fahrzeug verfügbar ist.",
  chauffeuredLabel: "Mit Chauffeur",
  selfDriveLabel: "Selbst fahren",
  perHour: "/Stunde",
  perDay: "/Tag",
};

export const HIRE_LANDINGS: HireLanding[] = [
  // ── /uk/hire/ford/mustang ────────────────────────────────────────────────
  {
    market: "uk",
    pathType: "model",
    makeSlug: "ford",
    modelSlug: "mustang",
    locale: "en",
    localeGroup: "hire-ford-mustang-uk",
    h1: "Ford Mustang Hire in the UK",
    metaTitle: "Ford Mustang Hire UK: Classic & Modern, Chauffeur or Self-Drive",
    metaDescription:
      "Hire a Ford Mustang in the UK from local owners: 1960s fastbacks and convertibles to modern GTs. Chauffeured for weddings and events, or self-drive.",
    heroIntro:
      "From 1965 fastbacks and V8 convertibles to Shelby-badged GT500s, the UK's Mustang owners list their cars here for weddings, proms, photo shoots and weekend drives. Most are offered chauffeured, with the owner driving while you enjoy the car; a smaller number are available to drive yourself.",
    heroImage: publicAssetUrl("cars/1034301/legacy-1056574_1280w.webp"),
    heroAlt: "1965 Ford Mustang convertible listed for hire in the UK",
    sections: [
      {
        heading: "Why the Mustang is Britain's favourite American hire car",
        body:
          "No American car translates to a British occasion quite like the first-generation Mustang. The long bonnet and burbling V8 read instantly on camera, and because early cars were built in huge numbers, the UK has a genuinely deep pool of them, from honest 1965 notchbacks to concours '68 convertibles. On BookAclassic you're hiring directly from the owner, which is why the same car often turns up better presented, better maintained and cheaper than through a traditional hire fleet. Listings run from 1964 right up to current-generation GTs, so you can match the car to the era of your event: a '60s fastback for a vintage wedding, a modern 5.0 for a prom entrance.",
      },
      {
        heading: "Chauffeured or self-drive?",
        body:
          "The overwhelming majority of UK Mustang listings are chauffeured; the owner drives you in their own car, typically charged by the hour with a two-to-three-hour minimum. That suits weddings and proms perfectly: no insurance hurdles, no worries about a left-hand-drive manual on the day, and an owner who knows exactly how their car photographs. A handful of owners also offer self-drive by the day. For self-drive expect a licence check, a minimum age (commonly 25–30 for classics), and a refundable security deposit agreed with the owner before handover.",
      },
      {
        heading: "What it costs",
        body:
          "Chauffeured Mustang hire in the UK starts at around £70 per hour, with most wedding packages landing between £250 and £500 for three to four hours of cover. Self-drive days start from roughly £400 for a classic. Exact pricing is set by each owner and shown on the listing; the live cards below always show current rates, and booking requests are free to send.",
      },
      {
        heading: "Where the cars are",
        body:
          "Mustangs are listed across the whole country. London, Manchester and Cardiff have the deepest choice, with owners in Preston, Cleckheaton, Magherafelt and dozens of smaller towns covering the rest of the map. Most chauffeured owners will travel a reasonable distance for a wedding; check the listing's location and message the owner with your venue postcode before booking.",
      },
    ],
    faqs: [
      {
        q: "Can I drive the Mustang myself?",
        a: "Some owners offer self-drive, but most UK Mustang listings are chauffeur-driven, with the owner driving their own car. Use the rental-type selector on each listing to see what's offered. Self-drive typically requires a full licence, a minimum age set by the owner (often 25+), and a refundable deposit.",
      },
      {
        q: "How much does Ford Mustang hire cost in the UK?",
        a: "Chauffeured hire starts around £70 per hour, and most owners quote a package for weddings (commonly £250–£500 for 3–4 hours). Self-drive classics start from roughly £400 per day. Every listing shows the owner's live rate.",
      },
      {
        q: "Are the classic Mustangs left-hand drive?",
        a: "Most 1960s cars are original US imports and therefore left-hand drive, though some UK-converted and modern right-hand-drive cars are listed too. The listing photos and description say which; with a chauffeured booking, it makes no difference to you.",
      },
      {
        q: "Can I hire a Mustang for a wedding with ribbons and photos included?",
        a: "Yes. Wedding hire is the most common booking. Owners typically include ribbons on request and stay for photographs. Mention your venue, timings and any decoration requests in the booking message so the owner can confirm.",
      },
      {
        q: "How far in advance should I book?",
        a: "For a Saturday wedding in peak season (May–September), 3–6 months ahead is sensible for a specific car. Outside peak dates, a few weeks is usually enough, and you can always send a request to check availability instantly.",
      },
    ],
    listingsQuery: { make: "Ford", model: "Mustang", country_code: "GB" },
    related: [
      { label: "1967 Ford Mustang model guide", href: "/models/ford-mustang-1967" },
      { label: "Wedding car hire in the UK", href: "/wedding-car-hire" },
      { label: "Porsche 911 hire in the UK", href: "/hire/porsche/911" },
      { label: "Jaguar E-Type hire in the UK", href: "/hire/jaguar/e-type" },
      { label: "All Ford listings", href: "/search", searchState: { make: "Ford", country_code: "GB" } },
    ],
    ui: EN_UI,
    updatedAt: "2026-07-17",
  },

  // ── /uk/hire/porsche/911 ────────────────────────────────────────────────
  {
    market: "uk",
    pathType: "model",
    makeSlug: "porsche",
    modelSlug: "911",
    locale: "en",
    h1: "Porsche 911 Hire in the UK",
    metaTitle: "Porsche 911 Hire UK: Air-Cooled Classics & Modern Icons",
    metaDescription:
      "Hire a Porsche 911 in the UK from private owners: air-cooled Carreras, Targas and modern 991s. Chauffeured experiences or self-drive days from £300.",
    heroIntro:
      "The UK's 911 owners list everything from 1970s RSR-tribute racers and impact-bumper SCs to Carrera 3.2s, Targas and modern 991s. Hire one chauffeured for an event, or take an air-cooled classic out on a self-drive day. The pool is deepest in and around London.",
    heroImage: publicAssetUrl("cars/1043586/legacy-1218711_1280w.webp"),
    heroAlt: "1979 Porsche 911 SC listed for hire in the UK",
    sections: [
      {
        heading: "Which 911 should you hire?",
        body:
          "\"911\" covers sixty years of continuous evolution, and the UK listings reflect nearly all of it. If you want the classic experience, with floor-hinged pedals, unassisted steering and that air-cooled flat-six chatter, look at the 1970s and '80s cars: SCs, Carrera 3.2s and Targas make up the heart of the pool. If you want pace and usability, owners also list water-cooled 996, 997 and 991-generation cars, including a Targa 4S. The special stuff surfaces too: RSR 3.0 tributes and rare Sport-specification Carreras have been listed by UK collectors. Each listing describes the specific car's history, so read past the model name.",
      },
      {
        heading: "Self-drive days: what owners expect",
        body:
          "The 911 is one of the most self-driven classics on the platform. It's compact, reliable and forgiving compared with most 1960s machinery. Owners typically ask for drivers aged 25–30+, a full licence held for several years, and a refundable deposit. Classic 911 gearboxes (particularly pre-1987 915 units) reward patience; if you've never driven one, say so in your booking message and most owners will happily give you a proper handover briefing. Self-drive days start from around £300.",
      },
      {
        heading: "Chauffeured 911 experiences",
        body:
          "A chauffeured 911 is a different product from a chauffeured Rolls: less procession, more passenger hot-lap. It suits milestone birthdays, surprise gifts and photography shoots where you want the car and its owner's knowledge in the frame. Hourly rates start around £80, and owners are usually flexible about routes: coast runs, the Cotswolds, or simply arriving somewhere memorable.",
      },
      {
        heading: "Where the cars are",
        body:
          "London dominates, with roughly a third of the UK's live 911 listings in or around the capital and further owners in Birmingham, Wetherby, Bathgate and across the Home Counties. For a weekend drive it's often worth widening your search radius: an hour's train ride can double your choice of cars.",
      },
    ],
    faqs: [
      {
        q: "How old do I need to be to self-drive a classic 911?",
        a: "Each owner sets their own minimum. 25 is common, and some ask for 30+ with classic-car experience for older or more valuable cars. The requirement is stated on the listing or confirmed when you send a request.",
      },
      {
        q: "How much does Porsche 911 hire cost in the UK?",
        a: "Chauffeured bookings start around £80 per hour. Self-drive days start from roughly £300 for an SC or Carrera 3.2, rising for rarer cars. Every listing shows the owner's live rate before you book.",
      },
      {
        q: "Are air-cooled 911s hard to drive?",
        a: "They demand more attention than a modern car, with slower gearshifts, firmer brakes, and weight at the back, but they're among the friendliest classics to drive. Owners give a handover briefing, and if you'd rather not drive, chauffeured listings put the owner behind the wheel.",
      },
      {
        q: "Can I hire a 911 as a gift for someone else to drive?",
        a: "Yes. This is one of the most common bookings. The gift recipient becomes the named driver and must meet the owner's age and licence requirements. Message the owner to arrange a voucher-style booking for a flexible date.",
      },
      {
        q: "Is insurance included?",
        a: "For chauffeured hire the owner's own insurance covers the drive. For self-drive, cover is arranged per booking; the owner will confirm the arrangement and any excess or deposit before you commit.",
      },
    ],
    listingsQuery: { make: "Porsche", model: "911", country_code: "GB" },
    related: [
      { label: "Air-cooled Porsche 911 model guide", href: "/models/porsche-911-air-cooled" },
      { label: "Ford Mustang hire in the UK", href: "/hire/ford/mustang" },
      { label: "Jaguar E-Type hire in the UK", href: "/hire/jaguar/e-type" },
      { label: "Wedding car hire in the UK", href: "/wedding-car-hire" },
    ],
    ui: EN_UI,
    updatedAt: "2026-07-17",
  },

  // ── /uk/hire/jaguar/e-type ──────────────────────────────────────────────
  {
    market: "uk",
    pathType: "model",
    makeSlug: "jaguar",
    modelSlug: "e-type",
    locale: "en",
    h1: "Jaguar E-Type Hire in the UK",
    metaTitle: "Jaguar E-Type Hire UK: Roadsters & Coupés, Chauffeur or Self-Drive",
    metaDescription:
      "Hire a Jaguar E-Type in the UK from private owners: Series 1 to Series 3 roadsters, coupés and 2+2s. Chauffeured wedding cars and self-drive experiences.",
    heroIntro:
      "Enzo Ferrari called it the most beautiful car ever made, and it remains Britain's definitive wedding and anniversary car. UK owners list Series 1, 2 and 3 E-Types, including roadsters, fixed-heads and 2+2s from 1962 to 1974, mostly chauffeured, with a few available for self-drive days.",
    heroImage: publicAssetUrl("cars/1026661/legacy-1013129_1280w.webp"),
    heroAlt: "1967 Jaguar E-Type listed for hire in the UK",
    sections: [
      {
        heading: "The E-Type as an occasion car",
        body:
          "The E-Type earns its place at weddings in a way few cars can: it's British, it's glamorous without being ostentatious, and its silhouette is recognised by every generation of guest. The UK pool spans the whole production run, from early Series 1 cars with covered headlights and usable Series 2s to V12 Series 3 roadsters whose extra cabin space actually suits a wedding dress far better than the snug six-cylinder cars. If rear seats matter (a parent, a photographer), look for a 2+2. Because these are private collectors' cars rather than fleet vehicles, condition tends to be genuinely high; many listings are concours or recently restored cars.",
      },
      {
        heading: "Chauffeured hire: how it works",
        body:
          "Nearly every UK E-Type listing is chauffeur-driven, with hourly rates from around £180 and typical wedding packages of three to four hours. The owner arrives early, ribbons the car if you wish, drives the route you've agreed, and stays for photographs. E-Types are low cars with long doors, making them brilliant for photos; owners are practised at helping passengers in formal wear step in and out gracefully.",
      },
      {
        heading: "Self-drive E-Type days",
        body:
          "A small number of owners offer self-drive from roughly £595 per day, usually to experienced drivers aged 30+ with a substantial refundable deposit. If a self-drive E-Type is the dream, plan ahead: with under twenty cars live nationally at any moment and only a few offered self-drive, the diary fills quickly in summer. A booking request costs nothing and confirms availability fastest.",
      },
      {
        heading: "Availability across the UK",
        body:
          "London has the most listings, with cars also in Cambridge, Cardiff, Chester and Bedford. Owners regularly travel to venues within an hour or two of home for chauffeured bookings; send your venue and timings with your request and the owner will confirm coverage.",
      },
    ],
    faqs: [
      {
        q: "How much does E-Type wedding hire cost?",
        a: "Chauffeured E-Type hire starts around £180 per hour, and most weddings book a 3–4 hour package. The live listings below show each owner's current rate; prices are set by the owner, not a central fleet.",
      },
      {
        q: "Roadster or coupé for a wedding?",
        a: "Roadsters photograph beautifully and make hood-down arrivals possible; V12 Series 3 cars have the widest cabins and easiest access in a dress. Fixed-head coupés keep hair intact in bad weather. Many couples choose a roadster with the hood decision made on the morning.",
      },
      {
        q: "Can I drive an E-Type myself?",
        a: "A few UK owners offer self-drive to experienced drivers, typically aged 30+, with a deposit. Most listings are chauffeured only, with the owner driving. Filter by rental type on the search page to see the self-drive cars.",
      },
      {
        q: "Do E-Types fit two passengers plus luggage?",
        a: "Two-seaters take two adults and soft bags only. The 2+2 adds small rear seats suitable for children or short trips. For a wedding, couple-plus-photographer usually means booking a 2+2 or a second car.",
      },
      {
        q: "What happens if it rains?",
        a: "Roadster owners bring the hood and plan around showers; they know their cars and check forecasts obsessively. Coupés are unaffected. Discuss a wet-weather plan with the owner before the day; they'll have one.",
      },
    ],
    listingsQuery: { make: "Jaguar", model: "E-Type", country_code: "GB" },
    related: [
      { label: "Jaguar E-Type model guide", href: "/models/jaguar-e-type" },
      { label: "Wedding car hire in the UK", href: "/wedding-car-hire" },
      { label: "Rolls-Royce Silver Shadow hire", href: "/hire/rolls-royce/silver-shadow" },
      { label: "Porsche 911 hire in the UK", href: "/hire/porsche/911" },
    ],
    ui: EN_UI,
    updatedAt: "2026-07-17",
  },

  // ── /uk/hire/land-rover/defender ────────────────────────────────────────
  {
    market: "uk",
    pathType: "model",
    makeSlug: "land-rover",
    modelSlug: "defender",
    locale: "en",
    h1: "Land Rover Defender Hire in the UK",
    metaTitle: "Land Rover Defender Hire UK: Classic 90s, 110s & Wedding Defenders",
    metaDescription:
      "Hire a Land Rover Defender in the UK: classic 90s and 110s, ex-military Wolfs and new Defenders. Chauffeured for rustic weddings or self-drive adventures.",
    heroIntro:
      "The Defender has become the definitive car for countryside weddings and outdoor celebrations: rugged, photogenic and unmistakably British. UK owners list everything from 1980s Ninety and One Ten workhorses and ex-military Wolf 110s to Heritage-edition 90s and the new-generation Defender.",
    heroImage: publicAssetUrl("cars/1044420/legacy-1133655_1280w.webp"),
    heroAlt: "Classic Land Rover Defender 110 listed for hire in the UK",
    sections: [
      {
        heading: "Why Defenders own the rustic wedding",
        body:
          "Barn venues, farm weddings, and festival-style celebrations suit the Defender, which fits where a limousine looks absurd. It carries a bride across a muddy field without drama, the flat sides take ribbons and florals beautifully, and the boxy silhouette photographs like nothing else against a hedgerow. The UK pool is one of the deepest on the platform: classic 90s and 110s from 1985 onward, genuine ex-MoD Wolf 110s (including a Ceremonial Wolf), soft-tops, double-cab pick-ups and a growing number of 2020-on new Defenders for those who want the look with modern comfort.",
      },
      {
        heading: "Classic or new Defender?",
        body:
          "Classic Defenders (pre-2016) are the character choice: leaf-sprung early cars and coil-sprung later ones, all noisy, charming and slow in the best way. They suit short chauffeured journeys, such as church to venue or venue to photos. The new Defender is the practical choice for longer runs or when grandparents are travelling too: air-conditioned, smooth and still every inch a Defender in the photographs. Plenty of couples book one of each, a classic 90 for the couple and a new 110 for the wedding party.",
      },
      {
        heading: "Costs and booking",
        body:
          "Chauffeured Defender hire starts at around £100 per hour, and self-drive from roughly £100–£150 per day for an honest classic, among the most affordable classics to hire in the UK. Owners set their own rates; the live cards below show today's prices. For peak-season Saturday weddings, book two to four months ahead, especially for soft-top and Heritage cars.",
      },
      {
        heading: "Where to find them",
        body:
          "London has the biggest cluster, with strong coverage around Sheffield, Cheltenham, Liverpool and the South West. Defender owners skew rural, which is exactly where the weddings are. Most owners quote travel to venues within an hour of home; include your venue postcode in the booking request.",
      },
    ],
    faqs: [
      {
        q: "How much does Defender wedding hire cost?",
        a: "Chauffeured bookings start around £100 per hour, with typical wedding packages of £250–£450. Self-drive classics start near £100–£150 per day. Each listing shows the owner's live rate.",
      },
      {
        q: "Can a Defender handle a muddy or off-road venue?",
        a: "Yes. That's the point. Tell the owner about the terrain in advance (a working farm track vs. a lawn) so they can plan tyres and cleaning. Most owners are delighted to use the car as intended.",
      },
      {
        q: "Are soft-top Defenders available?",
        a: "A number of UK listings are soft-tops or convertibles, ideal for summer weddings and photos. They're the first to book out for peak Saturdays, so reserve early.",
      },
      {
        q: "Can I self-drive a classic Defender?",
        a: "Yes, more owners offer self-drive on Defenders than on most classics. Expect a licence check, a minimum age around 25, and a refundable deposit. Classic Defenders are physical to drive, with heavy steering and long gear throws, which is half the fun.",
      },
      {
        q: "How many passengers fit?",
        a: "A classic 90 seats 2–4; a 110 station wagon can carry up to 7 (bench seats in the rear on older cars). New Defenders seat 5–7 in comfort. Check the listing's passenger count for the specific car.",
      },
    ],
    listingsQuery: { make: "Land Rover", model: "Defender", country_code: "GB" },
    related: [
      { label: "Wedding car hire in the UK", href: "/wedding-car-hire" },
      { label: "Mercedes-Benz hire in the UK", href: "/hire/mercedes-benz" },
      { label: "Volkswagen Beetle hire in the UK", href: "/hire/volkswagen/beetle" },
      { label: "All Land Rover listings", href: "/search", searchState: { make: "Land Rover", country_code: "GB" } },
    ],
    ui: EN_UI,
    updatedAt: "2026-07-17",
  },

  // ── /uk/hire/mercedes-benz ──────────────────────────────────────────────
  {
    market: "uk",
    pathType: "make",
    makeSlug: "mercedes-benz",
    locale: "en",
    h1: "Classic Mercedes-Benz Hire in the UK",
    metaTitle: "Classic Mercedes-Benz Hire UK: Pagodas, Fintails, SLs & More",
    metaDescription:
      "Hire a classic Mercedes-Benz in the UK from private owners: 280SL Pagodas, Fintail saloons, W111 coupés and R107 SLs. Chauffeured or self-drive.",
    heroIntro:
      "Mercedes-Benz is one of the best-represented makes on BookAclassic in the UK. The selection runs from 1950s Ponton saloons and 1960s Fintails through 280SL Pagodas and stately 280 SE limousines to 1980s R107 SLs, covering the full sweep of Stuttgart's golden era.",
    heroImage: publicAssetUrl("cars/1026293/legacy-1146761_1280w.webp"),
    heroAlt: "1967 Mercedes-Benz Fintail listed for hire in the UK",
    sections: [
      {
        heading: "The make for every occasion",
        body:
          "No other manufacturer covers the occasions like classic Mercedes. For weddings, the W111 and W108 saloons (220s, 280 SEs, and Fintails) offer rear-seat space, upright elegance and doors that open wide for a dress. For romantic drives and anniversaries, the W113 280SL Pagoda is perhaps the most beautiful open Mercedes ever built, and the UK has an unusually strong supply of them. For a relaxed weekend cruise, the 1970s–80s R107 SLs (280SL, 380SL, 500SL) are as dependable as classics get. The engineering reputation is deserved: these cars were built to outlast their owners, which is why so many survive in genuinely usable condition.",
      },
      {
        heading: "Chauffeured Mercedes for weddings and events",
        body:
          "The big saloons and limousines are the natural chauffeured cars. Many UK owners specialise in wedding work with the 280 SE and Fintail models, including ribbons and photographs. Hourly rates vary widely with the car's rarity, and wedding packages typically cover three to four hours. Because the pool is so deep, you can often match the car's colour to your theme; black, silver, white and dark blue examples are all listed.",
      },
      {
        heading: "Self-drive: the sensible classics",
        body:
          "Mercedes classics are among the most confidently self-driven cars on the platform. Automatic gearboxes are common, the driving position is sane, and everything works. Self-drive days start from around £30–£100 depending on the car, with SLs commanding more. Owners ask for the usual: full licence, minimum age typically 25+, and a refundable deposit.",
      },
      {
        heading: "Where the cars are",
        body:
          "London is the centre of gravity, followed by Manchester, Leeds and Glasgow, but the depth of the selection means almost every region has cars. If you're outside the big cities, search by your town first; there's a good chance a Pagoda or SL lives closer than you think.",
      },
    ],
    faqs: [
      {
        q: "Which classic Mercedes is best for a wedding?",
        a: "The W111/W108 saloons (280 SE, 220 Fintail) are the traditional choice, with spacious rear seats, elegant lines and easy entry in formal wear. For a two-person arrival, a 280SL Pagoda with the hood down is unbeatable.",
      },
      {
        q: "How much does classic Mercedes hire cost in the UK?",
        a: "It's the widest price range on the platform because the pool is so varied. Self-drive days start from around £30 for entry classics, while rare Pagodas and limousines command premium rates. Chauffeured wedding packages typically run £200–£500. Each listing shows live pricing.",
      },
      {
        q: "Are automatic classic Mercedes available?",
        a: "Yes. A large share of the pool, especially the saloons and R107 SLs, are automatic. That makes them ideal first classics to self-drive. Filter or check the listing's transmission field.",
      },
      {
        q: "Can I hire a Mercedes Pagoda specifically?",
        a: "Yes. The UK usually has multiple W113 Pagodas (230SL/250SL/280SL) live. Search 'Pagoda' or browse the listings below; our Pagoda model guide covers what to look for.",
      },
      {
        q: "Do owners travel for weddings?",
        a: "Most chauffeured owners cover venues within an hour or so of home, and many will go further for a full-day booking. Send your venue postcode and timings in your request for a firm answer.",
      },
    ],
    listingsQuery: { make: "Mercedes-Benz", country_code: "GB" },
    related: [
      { label: "Mercedes Pagoda model guide", href: "/models/mercedes-pagoda" },
      { label: "Wedding car hire in the UK", href: "/wedding-car-hire" },
      { label: "Rolls-Royce Silver Shadow hire", href: "/hire/rolls-royce/silver-shadow" },
      { label: "Jaguar E-Type hire in the UK", href: "/hire/jaguar/e-type" },
    ],
    ui: EN_UI,
    updatedAt: "2026-07-17",
  },

  // ── /dk/hire/ford/mustang (Danish) ──────────────────────────────────────
  {
    market: "dk",
    pathType: "model",
    makeSlug: "ford",
    modelSlug: "mustang",
    locale: "da",
    localeGroup: "hire-ford-mustang-dk",
    h1: "Lej en Ford Mustang i Danmark",
    metaTitle: "Lej Ford Mustang i Danmark: klassiske og nye, med chauffør eller kør selv",
    metaDescription:
      "Lej en Ford Mustang direkte fra danske ejere: 60'er-cabrioleter, Shelby GT500 og nye GT'er. Med chauffør til bryllup og fest, eller kør selv-oplevelser.",
    heroIntro:
      "Fra originale 1966-cabrioleter og Shelby GT500 til nye 5.0 GT'er: danske Mustang-ejere udlejer deres biler til bryllupper, studenterkørsel, fotoshoots og køreture. De fleste biler lejes med ejeren bag rattet, og et par stykker kan du selv køre.",
    heroImage: publicAssetUrl("cars/1024525/legacy-1121141_1280w.webp"),
    heroAlt: "Ford Mustang Shelby GT500 udlejes i Danmark",
    sections: [
      {
        heading: "Danmarks amerikanerbil nummer ét",
        body:
          "Mustangen har en særlig plads i dansk bilkultur. Den er selve billedet på amerikansk V8-charme, og der findes flere velholdte eksemplarer herhjemme, end man skulle tro. På BookAclassic lejer du direkte af ejeren: private entusiaster fra Odense, Kolding, Toftlund og resten af landet, som passer deres biler bedre end noget udlejningsfirma. Udvalget spænder fra 1964 til i dag og omfatter 60'er-coupéer og cabrioleter, en ægte '67 Shelby GT500 og moderne GT'er, når det skal være ny bil med gammeldags lyd.",
      },
      {
        heading: "Med chauffør eller kør selv?",
        body:
          "Langt de fleste danske Mustang-annoncer er med chauffør, hvor ejeren kører, og du og dine gæster nyder turen. Det er den oplagte løsning til bryllupper og studenterkørsel: ingen forsikringsbøvl, ingen nerver bag rattet på den store dag, og en ejer der ved præcis, hvordan bilen tager sig bedst ud på billederne. Nogle få ejere tilbyder også kør selv-leje på dagsbasis. Regn med krav om gyldigt kørekort, en aldersgrænse (typisk 25+) og et depositum, der aftales med ejeren inden afhentning.",
      },
      {
        heading: "Hvad koster det?",
        body:
          "Mustang-leje med chauffør starter omkring 1.500 kr. i timen, og de fleste bryllupspakker lander på 4.000–7.000 kr. for tre-fire timers kørsel. Kør selv-dage starter omkring 6.000 kr. for en klassiker. Priserne fastsættes af den enkelte ejer og vises på annoncen; kortene herunder viser altid de aktuelle priser, og det er gratis at sende en forespørgsel.",
      },
      {
        heading: "Hvor står bilerne?",
        body:
          "Mustangerne er spredt over hele landet med god dækning i Trekantområdet og på Fyn (Odense, Kolding, Toftlund og Brørup) samt biler på Sjælland omkring Holbæk. De fleste ejere kører gerne et stykke for et bryllup; skriv adressen på jeres lokation i forespørgslen, så bekræfter ejeren, om det kan lade sig gøre.",
      },
    ],
    faqs: [
      {
        q: "Må jeg selv køre Mustangen?",
        a: "Nogle ejere tilbyder kør selv-leje, men de fleste danske annoncer er med chauffør, hvor ejeren kører sin egen bil. Ved kør selv skal du typisk være 25+, have haft kørekort i flere år og betale et depositum, som du får igen efter turen.",
      },
      {
        q: "Hvad koster det at leje en Ford Mustang i Danmark?",
        a: "Med chauffør fra ca. 1.500 kr. i timen; bryllupspakker ligger typisk på 4.000–7.000 kr. for 3–4 timer. Kør selv fra ca. 6.000 kr. pr. dag. Hver annonce viser ejerens aktuelle pris.",
      },
      {
        q: "Kan jeg leje en Mustang til studenterkørsel?",
        a: "Ja. En åben Mustang er en klassiker til studenterkørsel. Book i god tid: juni-datoerne ryger først, og cabrioleterne er de mest efterspurgte. Skriv rute og antal timer i forespørgslen.",
      },
      {
        q: "Er bilerne originale amerikanske?",
        a: "De fleste klassiske Mustanger i Danmark er importerede originaler med venstrestyring. Annoncens billeder og beskrivelse fortæller bilens historie, og med chauffør betyder styringen ingenting for din oplevelse.",
      },
      {
        q: "Hvor lang tid i forvejen skal jeg booke?",
        a: "Til bryllupper i højsæsonen (maj–september) er 3–6 måneder fornuftigt, hvis du vil have en bestemt bil. Uden for sæsonen er et par uger som regel nok, og en forespørgsel koster ikke noget.",
      },
    ],
    listingsQuery: { make: "Ford", model: "Mustang", country_code: "DK" },
    related: [
      { label: "Se alle Ford-annoncer i Danmark", href: "/search", searchState: { make: "Ford", country_code: "DK" } },
      { label: "Ford Mustang hire in the UK", href: "https://www.bookaclassic.com/uk/hire/ford/mustang" },
      { label: "Alle klassiske biler i Danmark", href: "/search" },
    ],
    ui: DA_UI,
    updatedAt: "2026-07-17",
  },

  // ── /ca/hire/chevrolet ──────────────────────────────────────────────────
  {
    market: "ca",
    pathType: "make",
    makeSlug: "chevrolet",
    locale: "en",
    h1: "Classic Chevrolet Hire in Canada",
    metaTitle: "Classic Chevrolet Rental Canada: Bel Airs, Chevelles, Corvettes & Camaros",
    metaDescription:
      "Rent a classic Chevrolet in Canada from local owners: '56 Bel Airs, Chevelles, Corvettes, Camaros and Impalas. Chauffeured for weddings or self-drive.",
    heroIntro:
      "From tri-five Bel Airs and '68 Chevelles to C3 Corvettes, Camaro Z28s and four-door Impala hardtops, Canada's Chevrolet owners list the full sweep of GM's golden age. Most cars are offered chauffeured for weddings, grads and photo shoots, with self-drive available on a handful.",
    heroImage: publicAssetUrl("cars/1049933/legacy-1177786_1280w.webp"),
    heroAlt: "1956 Chevrolet Bel Air listed for hire in Canada",
    sections: [
      {
        heading: "Canada's broad Chevrolet selection",
        body:
          "Chevrolet built the cars Canadians actually grew up with, and the selection reflects it: from a 1939 pre-war survivor to muscle-era Chevelles and Camaros. The icons are all here: a '56 Bel Air in period two-tone, a '58 Corvette, a '64 Corvair Spyder convertible, '72 and '68 Chevelles, a '73 Camaro Z28 RS and a '67 Impala four-door hardtop of Supernatural fame. Because every car belongs to a private owner rather than a rental fleet, the descriptions read like the enthusiast biographies they are. Ask the owner anything about the car's history and you'll get a real answer.",
      },
      {
        heading: "Weddings, grads and photo shoots",
        body:
          "Chauffeured hire is the default in Canada: the owner drives, you arrive. A two-tone Bel Air or an Impala hardtop makes a spectacular wedding car, and grad-season bookings (May–June) are the busiest weeks of the year. Reserve those dates months out. Hourly rates start around CA$120, and most owners quote a flat package for weddings covering the ceremony run, photos and the reception arrival.",
      },
      {
        heading: "Self-drive muscle",
        body:
          "A smaller set of owners offer self-drive by the day from around CA$175, mostly on the driver-focused cars: Chevelles, Camaros and Corvettes. Expect a minimum age of 25+, a full licence and a refundable security deposit. If you've never driven a big-block with drum brakes, say so; owners would far rather brief you properly than worry.",
      },
      {
        heading: "Where the cars are",
        body:
          "British Columbia's Lower Mainland (Langley, Abbotsford) and the Greater Toronto Area (Toronto, Brampton, London) have the strongest clusters, with cars in Alberta around Fort Saskatchewan and more across the Prairies. Distances are real in Canada, so check the listing's city and confirm travel with the owner before booking a venue two hours away.",
      },
    ],
    faqs: [
      {
        q: "How much does classic Chevrolet rental cost in Canada?",
        a: "Chauffeured bookings start around CA$120 per hour, with typical wedding packages of CA$400–$800. Self-drive days start near CA$175. Every listing shows the owner's live rate in Canadian dollars.",
      },
      {
        q: "Can I rent a Chevrolet for my wedding or grad?",
        a: "Yes. Weddings and grad season are the most popular bookings. Owners decorate on request and stay for photos. Grad dates in May–June book out first, so send requests early.",
      },
      {
        q: "Can I drive the car myself?",
        a: "Some owners offer self-drive, mostly on Chevelles, Camaros and Corvettes, with a 25+ age requirement and deposit. Most Bel Airs and Impalas are chauffeured only, with the owner driving.",
      },
      {
        q: "Are these cars winter-available?",
        a: "Most classic Chevs hibernate from November to April, as owners rarely expose them to salt. Book summer and early-fall dates; some indoor photo-shoot bookings are possible year-round.",
      },
      {
        q: "Which model suits a vintage-themed event?",
        a: "For 1950s Americana, the Bel Air is definitive. For muscle-era events, a Chevelle SS or Camaro. The '67 Impala four-door hardtop is a cult favourite for TV-themed shoots.",
      },
    ],
    listingsQuery: { make: "Chevrolet", country_code: "CA" },
    related: [
      { label: "All Chevrolet listings in Canada", href: "/search", searchState: { make: "Chevrolet", country_code: "CA" } },
      { label: "Noleggio Chevrolet in Italia", href: "https://www.bookaclassic.com/it/hire/chevrolet" },
      { label: "All classic cars in Canada", href: "/search" },
    ],
    ui: EN_UI,
    updatedAt: "2026-07-17",
  },

  // ── /it/hire/chevrolet (Italian) ────────────────────────────────────────
  {
    market: "it",
    pathType: "make",
    makeSlug: "chevrolet",
    locale: "it",
    h1: "Noleggio Chevrolet d'Epoca in Italia",
    metaTitle: "Noleggio Chevrolet d'Epoca: Corvette, Bel Air e Camaro con Autista",
    metaDescription:
      "Noleggia una Chevrolet d'epoca in Italia da proprietari privati: Corvette C3, Bel Air, Camaro e rarità anni '20. Con autista per matrimoni ed eventi, o guida tu.",
    heroIntro:
      "Dalle Corvette Stingray C3 alle Bel Air anni '50, fino a una Torpedo del 1928: i proprietari italiani mettono a noleggio le loro Chevrolet per matrimoni, eventi, servizi fotografici e tour. La maggior parte delle auto si noleggia con il proprietario alla guida; alcune anche in formula guida tu.",
    heroImage: publicAssetUrl("cars/1052183/legacy-1193626_1280w.webp"),
    heroAlt: "Chevrolet Corvette C3 d'epoca a noleggio in Italia",
    sections: [
      {
        heading: "Il fascino americano, dal Duomo al Colosseo",
        body:
          "Una Chevrolet d'epoca in un centro storico italiano è un contrasto che funziona sempre: le cromature americane contro la pietra antica, il V8 che rimbomba tra i vicoli. In Italia il parco disponibile è sorprendentemente vario: Corvette C3 degli anni '70 e '80 (comprese le Stingray), una Bel Air del 1957, Camaro moderne per chi vuole prestazioni, un furgone Chevy Van G30 per shooting particolari e perfino una Torpedo del 1928 per matrimoni in stile Gatsby. Ogni auto appartiene a un collezionista privato, non a una flotta: la cura e la storia di ogni esemplare si vedono nell'annuncio, e potete chiedere direttamente al proprietario qualsiasi dettaglio.",
      },
      {
        heading: "Con autista: matrimoni ed eventi",
        body:
          "La formula più diffusa in Italia è il noleggio con autista: il proprietario guida la sua auto, voi vi godete l'arrivo. Per un matrimonio è la scelta naturale, senza pensieri per assicurazione o parcheggio e con un autista che conosce l'auto e sa come farla rendere al meglio nelle fotografie. Le tariffe orarie partono da circa 150 € e i pacchetti matrimonio coprono in genere tre-quattro ore: cerimonia, servizio fotografico e arrivo al ricevimento. Nastri e allestimenti si concordano direttamente con il proprietario.",
      },
      {
        heading: "Guida tu: la Corvette sulle strade giuste",
        body:
          "Alcuni proprietari offrono anche la formula guida tu, a partire da circa 380 € al giorno, perfetta per un regalo di anniversario o un weekend sulle strade del Chianti o del lago di Como. Servono patente valida da alcuni anni, un'età minima stabilita dal proprietario (spesso 25-30 anni) e un deposito cauzionale rimborsabile. Se non avete mai guidato un V8 americano d'epoca, ditelo: il proprietario sarà felice di farvi un briefing come si deve.",
      },
      {
        heading: "Dove si trovano le auto",
        body:
          "Roma e Firenze hanno la scelta più ampia, con altre auto a Como, in Brianza, a Bolzano e nel Milanese. Molti proprietari si spostano volentieri per un matrimonio nel raggio di un'ora o due: indicate la località dell'evento nella richiesta e riceverete conferma di disponibilità e costi di trasferta.",
      },
    ],
    faqs: [
      {
        q: "Quanto costa noleggiare una Chevrolet d'epoca in Italia?",
        a: "Con autista si parte da circa 150 € l'ora; i pacchetti matrimonio si aggirano in genere sui 450-800 € per 3-4 ore. La formula guida tu parte da circa 380 € al giorno. Ogni annuncio mostra la tariffa aggiornata del proprietario.",
      },
      {
        q: "Posso guidare io l'auto?",
        a: "Alcuni proprietari offrono la formula guida tu, soprattutto su Corvette e Camaro. Servono patente valida, età minima (di norma 25+) e un deposito rimborsabile. La maggior parte delle Bel Air e delle auto anteguerra si noleggia solo con autista.",
      },
      {
        q: "La Chevrolet è adatta come auto per il matrimonio?",
        a: "Assolutamente: una Bel Air o una Torpedo del 1928 sono scelte scenografiche e fuori dal comune. Il proprietario resta per le fotografie e l'allestimento con nastri si concorda in anticipo.",
      },
      {
        q: "Con quanto anticipo devo prenotare?",
        a: "Per un matrimonio tra maggio e settembre conviene muoversi 3-6 mesi prima, soprattutto per un modello specifico. Fuori stagione bastano poche settimane; inviare una richiesta è gratuito.",
      },
      {
        q: "Le auto possono circolare nelle ZTL dei centri storici?",
        a: "Per i matrimoni molti comuni rilasciano permessi ZTL temporanei; in genere se ne occupa chi organizza o il proprietario dell'auto, che conosce la prassi locale. Chiaritelo nella richiesta indicando chiesa e location.",
      },
    ],
    listingsQuery: { make: "Chevrolet", country_code: "IT" },
    related: [
      { label: "Tutte le Chevrolet in Italia", href: "/search", searchState: { make: "Chevrolet", country_code: "IT" } },
      { label: "Classic Chevrolet hire in Canada", href: "https://www.bookaclassic.com/ca/hire/chevrolet" },
      { label: "Tutte le auto d'epoca in Italia", href: "/search" },
    ],
    ui: IT_UI,
    updatedAt: "2026-07-17",
  },

  // ── /uk/wedding-car-hire ────────────────────────────────────────────────
  {
    market: "uk",
    pathType: "wedding",
    locale: "en",
    h1: "Classic Wedding Car Hire in the UK",
    metaTitle: "Classic Wedding Car Hire UK: Chauffeur Cars from Local Owners",
    metaDescription:
      "Hire a classic wedding car in the UK direct from its owner: Rolls-Royce, E-Types, Beetles, Defenders and more. Chauffeured packages with ribbons and photos.",
    heroIntro:
      "Nine out of ten wedding bookings on BookAclassic are chauffeured: the owner drives their own car, ribboned and prepared, and stays for the photographs. With thousands of classics listed across the UK, from Silver Shadows to soft-top Defenders, the right car for your day is almost certainly nearby.",
    heroImage: publicAssetUrl("cars/1033723/legacy-1058227_1280w.webp"),
    heroAlt: "1952 Bentley MK VI wedding car listed in the UK",
    sections: [
      {
        heading: "Why hire direct from the owner",
        body:
          "Traditional wedding-car companies run small fleets worked hard every Saturday. On BookAclassic you book the owner's personal car, usually pampered, often concours, and driven by the one person who cares about it most. That shows in the photographs and in the price: without fleet overheads, owner packages are typically 20–40% below trade rates for a comparable car. It also means real choice. Rather than 'a white Rolls or a Beauford', you can pick from E-Type roadsters, Mercedes Fintails, Silver Shadows, VW Beetles and camper vans, Defenders for farm venues, or a Mustang for something louder.",
      },
      {
        heading: "Matching the car to the wedding",
        body:
          "The classic formula: a stately saloon (Rolls-Royce Silver Shadow, Mercedes 280 SE, Daimler) for the bride and father, arriving unhurried. The modern reality is more varied. Couples book soft-top Defenders for barn weddings, pastel Beetles for village fetes, and open E-Types for two-person escapes between ceremony and reception. Think about access in a dress (four-door saloons win), the photographs you want (convertibles win), and the venue terrain (Defenders win). Our make and model pages below show live availability for the most-booked wedding cars.",
      },
      {
        heading: "What a wedding package includes",
        body:
          "A typical owner package covers three to four hours: arrival at the preparation address, the journey to the ceremony, waiting time, the trip to the reception, and staged photographs throughout. Ribbons in your colour are near-universal on request; many owners add an umbrella, a bottle of fizz, or a second run for the wedding party. Packages generally run £200–£600 depending on the car, with rare machinery above that. Every listing shows the owner's rate, and requests are free.",
      },
      {
        heading: "Timing your booking",
        body:
          "Peak-season Saturdays (May to September) book out first. For a specific car, start six to nine months ahead. Midweek and off-season dates are far easier and often cheaper. When you enquire, include the ceremony time, both addresses and your photography plans; owners plan their day around yours, and a complete first message gets a same-day yes far more often.",
      },
    ],
    faqs: [
      {
        q: "How much does classic wedding car hire cost in the UK?",
        a: "Most owner packages run £200–£600 for three to four hours, depending on the car. A VW Beetle sits at the lower end, a Silver Shadow or E-Type towards the upper. Rare cars command more. Each listing shows live pricing.",
      },
      {
        q: "Is the car chauffeur-driven?",
        a: "Almost always. The owner drives their own car. That covers insurance, keeps the couple stress-free, and puts the person who knows the car best behind the wheel. Self-drive wedding hire exists but is rare.",
      },
      {
        q: "Are ribbons and decorations included?",
        a: "Ribbons are included or added on request by nearly every owner; tell them your colours. Floral arrangements and personalised plates can usually be arranged; agree details in the booking messages.",
      },
      {
        q: "What if the car breaks down on the day?",
        a: "Ask every owner their contingency plan. Good ones have one (a second car, a trusted local colleague, generous time buffers). Classic cars used for weddings are maintained obsessively precisely because the date can't move.",
      },
      {
        q: "Can I book two cars, one for the couple and one for the party?",
        a: "Yes, and it's common: a saloon for the bride and father plus a second car for bridesmaids, or a classic for the couple and a modern people-carrier separately. Send requests to both owners and align pickup times in the messages.",
      },
      {
        q: "How far will owners travel to a venue?",
        a: "Most cover venues within an hour of home; many go further for full-day bookings. Include both postcodes in your request and the owner will confirm coverage and any travel supplement.",
      },
    ],
    listingsQuery: { country_code: "GB", rental_type: "chauffeured" },
    related: [
      { label: "Rolls-Royce Silver Shadow hire", href: "/hire/rolls-royce/silver-shadow" },
      { label: "Jaguar E-Type hire in the UK", href: "/hire/jaguar/e-type" },
      { label: "VW Beetle hire in the UK", href: "/hire/volkswagen/beetle" },
      { label: "Land Rover Defender hire", href: "/hire/land-rover/defender" },
      { label: "The complete wedding car guide", href: "/hire/wedding-car-hire" },
    ],
    ui: EN_UI,
    updatedAt: "2026-07-17",
  },

  // ── /uk/hire/volkswagen/beetle ──────────────────────────────────────────
  {
    market: "uk",
    pathType: "model",
    makeSlug: "volkswagen",
    modelSlug: "beetle",
    locale: "en",
    h1: "Volkswagen Beetle Hire in the UK",
    metaTitle: "VW Beetle Hire UK: Classic Beetles & Cabriolets for Weddings and Days Out",
    metaDescription:
      "Hire a classic VW Beetle in the UK from local owners: 1950s Ovals to 1970s cabriolets. Chauffeured wedding packages from £100/hour or self-drive days.",
    heroIntro:
      "Britain's most affectionately regarded classic, the Beetle does weddings, proms and photo shoots with a charm no limousine can match. UK owners list cars from a 1956 Oval-window survivor through 1960s–70s saloons to Karmann cabriolets, most chauffeured and several self-drive.",
    heroImage: publicAssetUrl("cars/1050123/legacy-1209631_1280w.webp"),
    heroAlt: "Classic Volkswagen Beetle listed for hire in the UK",
    sections: [
      {
        heading: "The friendly face of classic hire",
        body:
          "Nobody photographs next to a Beetle without smiling, which is precisely why it has become one of the UK's most-booked wedding cars. The pool spans the whole story of the car: a genuine 1956 Oval, upright 1960s saloons in period pastels, 1970s 1303s with the curved screen, and the prize of the fleet, Karmann-built cabriolets, whose full-length folding roofs make them the definitive open-top wedding Beetle. Colour matters with Beetles, and the UK listings deliver: whites, creams, pastel blues and greens dominate, and most owners are happy to match ribbons to your palette.",
      },
      {
        heading: "Weddings, proms and small budgets",
        body:
          "Beetle hire is the most affordable route into classic wedding transport. Chauffeured packages start around £100 per hour, with typical three-hour wedding bookings from £250. That price advantage makes two-car bookings realistic: a cabriolet for the couple and a saloon for bridesmaids is a popular pairing. The Beetle's compact size is also a genuine practical asset at tight village churches and urban venues where a Silver Shadow needs a three-point turn.",
      },
      {
        heading: "Driving one yourself",
        body:
          "Several UK owners offer self-drive from around £650 for a weekend-style booking. The Beetle is one of the easiest classics to drive, with light controls, superb visibility and unburstable mechanicals. It's a brilliant first classic experience or anniversary gift. Expect the standard owner conditions: full licence, minimum age around 23–25, and a refundable deposit.",
      },
      {
        heading: "Where the cars are",
        body:
          "London leads with the most listings, and there are cars in York, Glasgow, Manchester, Horsham and East Molesey, a genuinely national spread. Beetle owners are a sociable bunch and many do multiple weddings a season; if your date is taken, ask the owner to recommend another local car. They usually know every Beetle in the county.",
      },
    ],
    faqs: [
      {
        q: "How much does VW Beetle wedding hire cost?",
        a: "Chauffeured hire starts around £100 per hour, with typical wedding packages from £250 for three hours, the most affordable classic wedding car category in the UK. Self-drive bookings start near £650. Each listing shows live rates.",
      },
      {
        q: "Are convertible Beetles available?",
        a: "Yes. Karmann cabriolets are listed in the UK and they're the most requested wedding Beetles. They book out first for summer Saturdays, so enquire early.",
      },
      {
        q: "How many passengers fit in a Beetle?",
        a: "Four seats, but realistically two adults in comfort plus two in the rear for short journeys. A bride in a full dress travels best in the front with the seat back. For larger parties, book a second car.",
      },
      {
        q: "Can I self-drive a classic Beetle?",
        a: "Yes. Several owners offer self-drive, and the Beetle is arguably the easiest classic to start with. Full licence, minimum age around 23–25 and a refundable deposit are the standard requirements.",
      },
      {
        q: "Can the owner match decorations to our wedding colours?",
        a: "Almost always. Ribbons in your colour are standard on request, and Beetle owners tend to enjoy the styling side. Share your palette and theme in the booking messages.",
      },
    ],
    listingsQuery: { make: "Volkswagen", model: "Beetle", country_code: "GB" },
    related: [
      { label: "VW Beetle model guide", href: "/models/volkswagen-beetle" },
      { label: "Wedding car hire in the UK", href: "/wedding-car-hire" },
      { label: "Land Rover Defender hire", href: "/hire/land-rover/defender" },
      { label: "Ford Mustang hire in the UK", href: "/hire/ford/mustang" },
    ],
    ui: EN_UI,
    updatedAt: "2026-07-17",
  },

  // ── /uk/hire/rolls-royce/silver-shadow ──────────────────────────────────
  {
    market: "uk",
    pathType: "model",
    makeSlug: "rolls-royce",
    modelSlug: "silver-shadow",
    locale: "en",
    h1: "Rolls-Royce Silver Shadow Hire in the UK",
    metaTitle: "Rolls-Royce Silver Shadow Hire UK: The Definitive Wedding Car",
    metaDescription:
      "Hire a Rolls-Royce Silver Shadow in the UK direct from its owner: Series I and II saloons from 1967–1980. Chauffeured wedding packages from £200/hour.",
    heroIntro:
      "For half a century the Silver Shadow has been what people picture when they say 'wedding car'. UK owners list Series I and Series II saloons spanning 1967 to 1980, chauffeur-driven, ribboned, and photographed outside more churches than any other car in Britain.",
    heroImage: publicAssetUrl("cars/1049532/legacy-1176004_1280w.webp"),
    heroAlt: "Rolls-Royce Silver Shadow I listed for hire in the UK",
    sections: [
      {
        heading: "Why the Shadow remains the wedding benchmark",
        body:
          "Crewe built over 30,000 Silver Shadows between 1965 and 1980. By Rolls-Royce standards, it was a mass-market car, which is exactly why it became the people's Rolls. It has everything the role demands: rear doors that open wide onto a flat floor, a tall roofline that protects a veil, self-levelling suspension that glides over speed bumps, and a silhouette every wedding photographer knows how to frame. The UK pool covers the run honestly, from early Series I cars from 1967–68 and mid-'70s Shadows to the improved Series II (1977-on) with its rubber-faced bumpers and rack-and-pinion steering.",
      },
      {
        heading: "Series I or Series II?",
        body:
          "Photographically they're close; in the details they differ. Series I cars (to 1976) carry the classic chrome-bumper face and thin-rim wheel, the purist's Shadow. Series II cars steer more precisely, ride marginally better and idle their 6.75-litre V8 even more quietly, which is useful for on-camera arrivals. Colour is the bigger decision: whites and silvers dominate the wedding trade, but a dark blue or claret Shadow against a stone church is quietly spectacular. The live listings below show what's currently available and where.",
      },
      {
        heading: "What a Shadow booking looks like",
        body:
          "Nearly all UK Shadow listings are chauffeured, with the owner driving. Rates typically start from £200 per hour, with wedding packages of three to four hours covering the ceremony run, photographs and the reception arrival. Owners in this segment are the most wedding-experienced on the platform: expect ribbons, umbrellas, route reconnaissance and an unflappable schedule. A couple of owners also offer self-drive by arrangement, from around £250, usually to drivers 30+ with a significant deposit.",
      },
      {
        heading: "Availability and booking windows",
        body:
          "Shadows are listed from London and the Home Counties through Birmingham, Stoke-on-Trent and Sheffield to Scotland, with over two dozen live cars on a typical day. Peak Saturdays go first and white cars go fastest: for a May–September wedding, secure the car six to nine months out. Midweek dates are dramatically easier and often discounted.",
      },
    ],
    faqs: [
      {
        q: "How much does Silver Shadow wedding hire cost?",
        a: "Chauffeured hire starts around £200 per hour, with most wedding packages between £400 and £700 for three to four hours. Each owner sets their own rate; the listings below show live pricing.",
      },
      {
        q: "How many passengers does a Silver Shadow carry?",
        a: "Four passengers besides the chauffeur, with generous rear legroom. The bride and father travel in the back with room for the dress. For bridesmaids, most couples book a second car.",
      },
      {
        q: "Can I drive a Silver Shadow myself?",
        a: "A small number of owners offer self-drive by arrangement, generally to drivers aged 30+ with a substantial refundable deposit. The overwhelming majority of bookings are chauffeured, which is also how the car was designed to be enjoyed.",
      },
      {
        q: "White, silver or a colour?",
        a: "White and silver are the traditional wedding choices and the most listed. Dark blue, claret and two-tone Shadows photograph beautifully against stone and greenery and stand out in an album. There's no wrong answer; browse the live cars and trust your eye.",
      },
      {
        q: "How far ahead should I book?",
        a: "Six to nine months for a peak-season Saturday, particularly for white cars. Off-season and midweek dates can often be secured within weeks. Sending a request is free and confirms availability immediately.",
      },
    ],
    listingsQuery: { make: "Rolls-Royce", model: "Silver Shadow", country_code: "GB" },
    related: [
      { label: "Silver Shadow model guide", href: "/models/rolls-royce-silver-shadow" },
      { label: "Wedding car hire in the UK", href: "/wedding-car-hire" },
      { label: "Mercedes-Benz hire in the UK", href: "/hire/mercedes-benz" },
      { label: "Jaguar E-Type hire in the UK", href: "/hire/jaguar/e-type" },
    ],
    ui: EN_UI,
    updatedAt: "2026-07-17",
  },

  // ═══ Task #7156 — expanded wedding landing footprint ══════════════════════
  // Curated market wedding hubs (EN) + native-language variants with native
  // slugs, paired via localeGroup for bidirectional hreflang. Inventory
  // references pulled from the 2026-08 wedding SEO audit; copy avoids hard
  // counts that rot and never claims platform insurance (owner-arranged only).

  // ── /dk/wedding-car-hire (EN) ────────────────────────────────────────────
  {
    market: "dk",
    pathType: "wedding",
    locale: "en",
    localeGroup: "wedding-dk",
    h1: "Classic Wedding Car Hire in Denmark",
    metaTitle: "Classic Wedding Car Hire Denmark: Chauffeur Cars from Local Owners",
    metaDescription:
      "Hire a classic wedding car in Denmark direct from its owner: American convertibles, vintage Mercedes and rare European classics, chauffeured for your day.",
    heroIntro:
      "Denmark is one of BookAclassic's strongest markets: hundreds of privately owned classics from Copenhagen to Aarhus and Aalborg, most offered chauffeured by the owners themselves. Book the car for the ceremony run, the photographs and the send-off, driven by the person who knows it best.",
    heroImage: publicAssetUrl("cars/1024606/legacy-1215579_1280w.webp"),
    heroAlt: "1968 Jaguar Mark II wedding car listed in Denmark",
    sections: [
      {
        heading: "Why book direct from a Danish owner",
        body:
          "Danish wedding-car hire has traditionally meant a handful of livery firms with white American convertibles. On BookAclassic you choose from private owners across the country: pastel Cadillacs and Mustang convertibles, stately Mercedes saloons, VW Beetles and campers, and genuine rarities that never appear in trade fleets. The owner drives, the car arrives prepared and ribboned, and without fleet overheads the price is usually well below trade rates. Every listing shows the owner's live rate and requests are free.",
      },
      {
        heading: "Matching the car to a Danish wedding",
        body:
          "For a classic church wedding, a large saloon (a Mercedes Fintail or a Jaguar) gives the bride an unhurried, dress-friendly arrival. Summer weddings by the coast suit an open American convertible; photographs from the back seat of a Cadillac on a June evening are hard to beat. For barn and manor venues in Jutland and Funen, owners regularly cover the whole day including the drive between ceremony and reception. Think about access in a dress, the pictures you want, and the distance between addresses, then send the details with your request.",
      },
      {
        heading: "Packages, timing and booking windows",
        body:
          "A typical Danish owner package covers three to four hours: pickup, the ceremony run, waiting time, photographs and the reception arrival. Ribbons are near-universal on request. May to September Saturdays book out months ahead. For a specific car, start six months early; midweek and off-season dates are far easier. Include the ceremony time, both addresses and your photo plans in the first message for a fast yes.",
      },
    ],
    faqs: [
      {
        q: "How much does classic wedding car hire cost in Denmark?",
        a: "Most owner packages run roughly 2,500–6,000 kr. for three to four hours depending on the car. Each listing shows the owner's live rate; sending a request is free.",
      },
      {
        q: "Is the car chauffeur-driven?",
        a: "Almost always. The owner drives their own car, which keeps the day stress-free and puts the person who knows the car best behind the wheel. Self-drive wedding hire exists but is rare.",
      },
      {
        q: "Do owners decorate the car?",
        a: "Ribbons in your colours are included or added on request by nearly every owner. Flowers and personal touches can usually be arranged in the booking messages.",
      },
      {
        q: "How far ahead should we book?",
        a: "Six months or more for a peak-season Saturday, especially for white convertibles. Off-season and midweek dates can often be secured within weeks.",
      },
      {
        q: "Do owners cover venues outside Copenhagen?",
        a: "Yes. Cars are listed across Denmark, and most owners cover venues within an hour of home, further for full-day bookings. Include both addresses in your request.",
      },
    ],
    listingsQuery: { country_code: "DK", rental_type: "chauffeured" },
    related: [
      { label: "Bryllupsbil: læs på dansk", href: "/bryllupsbil" },
      { label: "Ford Mustang hire in Denmark", href: "/hire/ford/mustang" },
      { label: "The complete wedding car guide", href: "/hire/wedding-car-hire" },
      { label: "Classic wedding car guide", href: "/guides/classic-wedding-car-hire-guide" },
    ],
    ui: EN_UI,
    updatedAt: "2026-08-02",
  },

  // ── /dk/bryllupsbil (DA) ─────────────────────────────────────────────────
  {
    market: "dk",
    pathType: "wedding",
    slug: "bryllupsbil",
    locale: "da",
    localeGroup: "wedding-dk",
    h1: "Lej en klassisk bryllupsbil med chauffør",
    metaTitle: "Bryllupsbil: lej en klassisk bryllupsbil med chauffør | BookAclassic",
    metaDescription:
      "Lej en klassisk bryllupsbil direkte fra ejeren: amerikanske cabrioleter, veteran-Mercedes og sjældne klassikere med chauffør i hele Danmark.",
    heroIntro:
      "Danmark er et af BookAclassics stærkeste markeder: hundredvis af privatejede klassikere fra København til Aarhus og Aalborg, langt de fleste udlejet med ejeren bag rattet. Book bilen til kirken, fotografierne og festen, kørt af den person, der kender den bedst.",
    heroImage: publicAssetUrl("cars/1039932/legacy-1099151_1280w.webp"),
    heroAlt: "1947 Cadillac Fleetwood bryllupsbil i Danmark",
    sections: [
      {
        heading: "Derfor skal du leje direkte af ejeren",
        body:
          "Traditionel bryllupskørsel betyder ofte et lille udlejningsfirma med et par hvide amerikanerbiler. På BookAclassic vælger du blandt private ejere i hele landet: pastelfarvede Cadillac'er og Mustang-cabrioleter, statelige Mercedes'er, VW-bobler og folkevognsrugbrød samt ægte sjældenheder, du aldrig finder hos udlejningsfirmaerne. Ejeren kører selv, bilen ankommer nypoleret og pyntet, og uden firmaomkostninger ligger prisen typisk pænt under markedsprisen. Hver annonce viser ejerens aktuelle pris, og det er gratis at sende en forespørgsel.",
      },
      {
        heading: "Vælg bilen efter brylluppet",
        body:
          "Til det klassiske kirkebryllup giver en stor saloon (en Mercedes 'vinge' eller en Jaguar) bruden en rolig ankomst med plads til kjolen. Sommerbryllupper ved kysten klæder en åben amerikaner; billeder fra bagsædet af en Cadillac en juniaften er svære at slå. Til lade- og herregårdsbryllupper i Jylland og på Fyn dækker mange ejere hele dagen inklusive turen mellem vielse og fest. Tænk på adgangen i kjolen, de billeder I ønsker, og afstanden mellem adresserne, og skriv detaljerne i forespørgslen.",
      },
      {
        heading: "Pakker, timing og hvornår du skal booke",
        body:
          "En typisk pakke dækker tre-fire timer: afhentning, turen til vielsen, ventetid, fotografering og ankomsten til festen. Bånd i jeres farver er næsten altid med på forespørgsel. Lørdage fra maj til september bookes måneder i forvejen. Vil I have en bestemt bil, så start et halvt år før; hverdage og lavsæson er langt lettere. Skriv vielsestidspunkt, begge adresser og jeres fotoplaner i første besked, så får I hurtigst et ja.",
      },
    ],
    faqs: [
      {
        q: "Hvad koster det at leje en bryllupsbil?",
        a: "De fleste pakker ligger på cirka 2.500–6.000 kr. for tre-fire timer afhængigt af bilen. Hver annonce viser ejerens aktuelle pris; det er gratis at sende en forespørgsel.",
      },
      {
        q: "Kører ejeren bilen?",
        a: "Næsten altid. Ejeren kører sin egen bil, hvilket holder dagen stressfri og sætter den person, der kender bilen bedst, bag rattet. Kør selv-udlejning til bryllup findes, men er sjælden.",
      },
      {
        q: "Pynter ejeren bilen?",
        a: "Bånd i jeres farver er med hos næsten alle ejere. Blomster og personlige detaljer kan som regel aftales i beskederne.",
      },
      {
        q: "Hvor lang tid i forvejen skal vi booke?",
        a: "Mindst seks måneder før en lørdag i højsæsonen, især for hvide cabrioleter. Lavsæson og hverdage kan ofte bookes med få ugers varsel.",
      },
      {
        q: "Kører ejerne også uden for København?",
        a: "Ja. Der er biler i hele landet, og de fleste ejere dækker steder inden for en times kørsel hjemmefra, længere ved heldagsbookinger. Skriv begge adresser i forespørgslen.",
      },
    ],
    listingsQuery: { country_code: "DK", rental_type: "chauffeured" },
    related: [
      { label: "Wedding car hire in Denmark (English)", href: "/wedding-car-hire" },
      { label: "Lej en Ford Mustang i Danmark", href: "/hire/ford/mustang" },
      { label: "Den store guide til bryllupsbiler", href: "/hire/wedding-car-hire" },
    ],
    ui: DA_UI,
    updatedAt: "2026-08-02",
  },

  // ── /se/wedding-car-hire (EN) ────────────────────────────────────────────
  {
    market: "se",
    pathType: "wedding",
    locale: "en",
    localeGroup: "wedding-se",
    h1: "Classic Wedding Car Hire in Sweden",
    metaTitle: "Classic Wedding Car Hire Sweden: Chauffeur Cars from Local Owners",
    metaDescription:
      "Hire a classic wedding car in Sweden direct from its owner: American convertibles, vintage Volvos and Mercedes, chauffeured from Stockholm to Malmö.",
    heroIntro:
      "Sweden's classic-car culture is among the deepest in Europe, and much of it is on BookAclassic: privately owned Cadillacs, Amazons, Mercedes saloons and open Mustangs from Stockholm to Gothenburg and Skåne, most offered chauffeured by the owner for weddings.",
    heroImage: publicAssetUrl("cars/1034370/legacy-1056971_1280w.webp"),
    heroAlt: "1966 Ford Mustang GT convertible wedding car listed in Sweden",
    sections: [
      {
        heading: "Why book direct from a Swedish owner",
        body:
          "Sweden has more classic American iron per capita than almost anywhere, but the trade wedding fleets show none of it. On BookAclassic you book the owner's personal car (a raggare-era Cadillac, a concours Volvo Amazon, a Mercedes W111 or an open Mustang), prepared, ribboned and driven by the owner. Without fleet overheads, packages typically undercut trade rates while the cars themselves are better kept. Requests are free and every listing shows a live price.",
      },
      {
        heading: "Matching the car to a Swedish wedding",
        body:
          "A midsummer wedding in the archipelago calls for an open convertible; a winter church wedding suits a stately saloon with proper heating. Volvo Amazons and PVs bring understated Swedish charm to countryside venues, while a white Cadillac remains the classic student-flak-to-wedding crossover everyone recognises. Consider dress access (four doors win), the photographs you want (open cars win) and the drive between ceremony and reception. Owners plan the route with you.",
      },
      {
        heading: "Packages, timing and booking windows",
        body:
          "A typical owner package covers three to four hours: pickup, the ceremony run, waiting time, photographs and the reception arrival, with ribbons on request. June to August Saturdays, especially around midsummer, book out first; start six months ahead for a specific car. Off-season and midweek dates are far easier. Include the ceremony time, both addresses and your photo plans in the first message.",
      },
    ],
    faqs: [
      {
        q: "How much does classic wedding car hire cost in Sweden?",
        a: "Most owner packages run roughly 3,000–7,000 kr for three to four hours depending on the car. Each listing shows the owner's live rate; sending a request is free.",
      },
      {
        q: "Is the car chauffeur-driven?",
        a: "Almost always. The owner drives their own car, keeping the day stress-free with the person who knows the car best behind the wheel.",
      },
      {
        q: "Do owners decorate the car?",
        a: "Ribbons in your colours are near-universal on request; flowers and personal touches can usually be arranged in the booking messages.",
      },
      {
        q: "How far ahead should we book?",
        a: "Six months or more for a summer Saturday, especially around midsummer. Off-season and midweek dates can often be secured within weeks.",
      },
      {
        q: "Do owners cover venues outside Stockholm?",
        a: "Yes. Cars are listed from Skåne to Norrland. Most owners cover venues within an hour of home, further for full-day bookings.",
      },
    ],
    listingsQuery: { country_code: "SE", rental_type: "chauffeured" },
    related: [
      { label: "Bröllopsbil: läs på svenska", href: "/brollopsbil" },
      { label: "The complete wedding car guide", href: "/hire/wedding-car-hire" },
      { label: "Classic wedding car guide", href: "/guides/classic-wedding-car-hire-guide" },
    ],
    ui: EN_UI,
    updatedAt: "2026-08-02",
  },

  // ── /se/brollopsbil (SV) ─────────────────────────────────────────────────
  {
    market: "se",
    pathType: "wedding",
    slug: "brollopsbil",
    locale: "sv",
    localeGroup: "wedding-se",
    h1: "Hyr en klassisk bröllopsbil med chaufför",
    metaTitle: "Bröllopsbil: hyr en klassisk bröllopsbil med chaufför | BookAclassic",
    metaDescription:
      "Hyr en klassisk bröllopsbil direkt av ägaren: amerikanska cabrioleter, veteran-Volvo och Mercedes med chaufför, från Stockholm till Malmö.",
    heroIntro:
      "Sveriges bilkultur är en av Europas djupaste, och mycket av den finns på BookAclassic: privatägda Cadillacs, Amazoner, Mercedes och öppna Mustanger från Stockholm till Göteborg och Skåne, de flesta uthyrda med ägaren bakom ratten.",
    heroImage: publicAssetUrl("cars/1048958/legacy-1222188_1280w.webp"),
    heroAlt: "Volvo Amazon bröllopsbil i Sverige",
    sections: [
      {
        heading: "Därför ska du hyra direkt av ägaren",
        body:
          "Traditionell bröllopskörning betyder ofta några få vita bilar från en hyrfirma. På BookAclassic väljer du bland privata ägare i hela landet: en Cadillac från raggarepoken, en utställningsfin Volvo Amazon, en Mercedes W111 eller en öppen Mustang, förberedd, dekorerad och körd av ägaren själv. Utan firmakostnader ligger priset oftast under marknadspriset, samtidigt som bilarna är bättre omhändertagna. Varje annons visar ägarens aktuella pris och det är gratis att skicka en förfrågan.",
      },
      {
        heading: "Välj bil efter bröllopet",
        body:
          "Ett midsommarbröllop i skärgården kräver en öppen cabriolet; ett vinterbröllop i kyrkan passar en stor sedan med riktig värme. Volvo Amazon och PV ger lantliga bröllop en stillsam svensk charm, medan en vit Cadillac är klassikern alla känner igen. Tänk på hur lätt det är att kliva i och ur i klänning (fyra dörrar vinner), vilka bilder ni vill ha (öppna bilar vinner) och avståndet mellan vigsel och fest. Ägaren planerar rutten med er.",
      },
      {
        heading: "Paket, timing och när ni bör boka",
        body:
          "Ett typiskt paket täcker tre-fyra timmar: upphämtning, färden till vigseln, väntetid, fotografering och ankomsten till festen. Band i era färger ingår nästan alltid på begäran. Lördagar juni–augusti, särskilt kring midsommar, bokas först; börja ett halvår i förväg för en specifik bil. Lågsäsong och vardagar är mycket lättare. Skriv vigseltid, båda adresserna och era fotoplaner i första meddelandet.",
      },
    ],
    faqs: [
      {
        q: "Vad kostar det att hyra en bröllopsbil?",
        a: "De flesta paket ligger på cirka 3 000–7 000 kr för tre-fyra timmar beroende på bil. Varje annons visar ägarens aktuella pris; det är gratis att skicka en förfrågan.",
      },
      {
        q: "Kör ägaren bilen?",
        a: "Nästan alltid. Ägaren kör sin egen bil, vilket håller dagen stressfri och sätter den som känner bilen bäst bakom ratten.",
      },
      {
        q: "Dekorerar ägaren bilen?",
        a: "Band i era färger ingår hos nästan alla ägare på begäran. Blommor och personliga detaljer kan oftast ordnas i meddelandena.",
      },
      {
        q: "Hur långt i förväg bör vi boka?",
        a: "Minst sex månader före en sommarlördag, särskilt kring midsommar. Lågsäsong och vardagar kan ofta bokas med några veckors varsel.",
      },
      {
        q: "Kör ägarna även utanför Stockholm?",
        a: "Ja. Det finns bilar från Skåne till Norrland. De flesta ägare täcker platser inom en timmes körning hemifrån, längre vid heldagsbokningar.",
      },
    ],
    listingsQuery: { country_code: "SE", rental_type: "chauffeured" },
    related: [
      { label: "Wedding car hire in Sweden (English)", href: "/wedding-car-hire" },
      { label: "Den stora guiden till bröllopsbilar", href: "/hire/wedding-car-hire" },
    ],
    ui: SV_UI,
    updatedAt: "2026-08-02",
  },

  // ── /no/wedding-car-hire (EN) ────────────────────────────────────────────
  {
    market: "no",
    pathType: "wedding",
    locale: "en",
    h1: "Classic Wedding Car Hire in Norway",
    metaTitle: "Classic Wedding Car Hire Norway: Chauffeur Cars from Local Owners",
    metaDescription:
      "Hire a classic wedding car in Norway direct from its owner: American convertibles and vintage European classics, chauffeured from Oslo to Bergen.",
    heroIntro:
      "From Oslo and the fjord towns to Trøndelag, Norwegian owners list their privately kept classics on BookAclassic, most offered chauffeured, ribboned and prepared for weddings, driven by the person who knows the car best.",
    heroImage: publicAssetUrl("cars/1022689/legacy-1000672_1280w.webp"),
    heroAlt: "1959 Bentley S1 wedding car listed in Norway",
    sections: [
      {
        heading: "Why book direct from a Norwegian owner",
        body:
          "Wedding-car choice in Norway has long meant whatever the local livery firm runs. On BookAclassic you book a private owner's personal car: American convertibles, vintage Mercedes and Volvos, and rarities that never reach trade fleets. The owner drives, the car arrives prepared, and without fleet overheads the package price is usually kinder than trade rates. Every listing shows a live price and sending a request is free.",
      },
      {
        heading: "Matching the car to a Norwegian wedding",
        body:
          "A summer wedding by a fjord is made for an open convertible and photographs with the water behind; a stone-church ceremony suits a stately saloon with room for the dress. Distances matter in Norway. If ceremony and reception are far apart, ask the owner about a full-day booking and plan the photo stops along the route together. Include both addresses and the ceremony time in your first message.",
      },
      {
        heading: "Packages, timing and booking windows",
        body:
          "A typical owner package covers three to four hours: pickup, the ceremony run, waiting time, photographs and the reception arrival, ribbons on request. The short, bright wedding season (May to September) concentrates demand, so book a specific car six months out. Off-season dates are far easier and often cheaper.",
      },
    ],
    faqs: [
      {
        q: "How much does classic wedding car hire cost in Norway?",
        a: "Most owner packages run roughly 3,000–7,000 kr for three to four hours depending on the car. Each listing shows the owner's live rate; requests are free.",
      },
      {
        q: "Is the car chauffeur-driven?",
        a: "Almost always. The owner drives their own car, keeping the day stress-free with the person who knows the car best at the wheel.",
      },
      {
        q: "Do owners decorate the car?",
        a: "Ribbons in your colours are near-universal on request; flowers and personal touches can usually be arranged in the booking messages.",
      },
      {
        q: "How far will owners travel?",
        a: "Most cover venues within an hour of home and further for full-day bookings, worth asking early given Norwegian distances. Include both addresses in your request.",
      },
    ],
    listingsQuery: { country_code: "NO", rental_type: "chauffeured" },
    related: [
      { label: "The complete wedding car guide", href: "/hire/wedding-car-hire" },
      { label: "Classic wedding car guide", href: "/guides/classic-wedding-car-hire-guide" },
    ],
    ui: EN_UI,
    updatedAt: "2026-08-02",
  },

  // ── /it/wedding-car-hire (EN) ────────────────────────────────────────────
  {
    market: "it",
    pathType: "wedding",
    locale: "en",
    localeGroup: "wedding-it",
    h1: "Classic Wedding Car Hire in Italy",
    metaTitle: "Classic Wedding Car Hire Italy: Chauffeur Cars from Local Owners",
    metaDescription:
      "Hire a classic wedding car in Italy direct from its owner: vintage Alfas, Fiats, Lancias and grand saloons, chauffeured from Milan to Sicily.",
    heroIntro:
      "Italy's wedding heartland runs on classic metal: privately owned Alfa Romeos, Fiat 500s, Lancias and grand saloons listed from Milan and Rome to Puglia and Sicily, most offered chauffeured by the owner, ideal for destination weddings as well as local ones.",
    heroImage: publicAssetUrl("cars/1050437/legacy-1227814_1280w.webp"),
    heroAlt: "Citroën DS 21 Cabriolet wedding car listed in Italy",
    sections: [
      {
        heading: "Why book direct from an Italian owner",
        body:
          "Italian wedding-car hire is dominated by agencies reselling the same few cars at agency prices. On BookAclassic you deal directly with the owner of the car (a Giulia Spider, a cream Fiat 500, a Lancia Flaminia or a Maserati saloon), who drives it themselves, prepared and ribboned. Direct booking means real availability, honest prices and a driver who knows every backroad to the church. For couples marrying in Italy from abroad, the booking messages work in English and owners are used to destination weddings.",
      },
      {
        heading: "Matching the car to an Italian wedding",
        body:
          "A Tuscan villa wedding photographs beautifully with an open Spider or a Flaminia; a southern church ceremony traditionally calls for a grand white saloon. The Fiat 500 is the crowd-pleaser, small enough for medieval lanes and charming in every album. Consider the terrain (hill towns favour smaller cars), dress access, and the drive between ceremony and reception. Owners routinely plan photo stops at viewpoints along the route.",
      },
      {
        heading: "Packages, timing and booking windows",
        body:
          "A typical package covers three to four hours: pickup, the ceremony run, waiting, photographs and the reception arrival, with ribbons and often a bottle of prosecco on request. May, June and September book out first. For destination weddings, secure the car as soon as the venue is fixed, six to nine months out. Include the ceremony time, both addresses and your photography plans in the first message.",
      },
    ],
    faqs: [
      {
        q: "How much does classic wedding car hire cost in Italy?",
        a: "Most owner packages run roughly €300–€800 for three to four hours depending on the car. A Fiat 500 sits at the lower end, with grand saloons and open Spiders towards the upper. Each listing shows live pricing.",
      },
      {
        q: "Is the car chauffeur-driven?",
        a: "Almost always. The owner drives their own car. That keeps the day stress-free and puts the person who knows the roads and the car best behind the wheel.",
      },
      {
        q: "We're marrying in Italy from abroad. Does that work?",
        a: "Yes, it's common. Owners are used to destination weddings, the messages work in English, and booking early (six to nine months) secures the car once your venue is fixed.",
      },
      {
        q: "Do owners decorate the car?",
        a: "Ribbons are near-universal on request, and many owners add flowers or a prosecco stop. Agree the details in the booking messages.",
      },
      {
        q: "How far will owners travel?",
        a: "Most cover venues within an hour of home, further for full-day bookings. Include both addresses in your request, and the owner confirms coverage and any travel supplement.",
      },
    ],
    listingsQuery: { country_code: "IT", rental_type: "chauffeured" },
    related: [
      { label: "Noleggio auto matrimonio: leggi in italiano", href: "/noleggio-auto-matrimonio" },
      { label: "Chevrolet hire in Italy", href: "/hire/chevrolet" },
      { label: "The complete wedding car guide", href: "/hire/wedding-car-hire" },
      { label: "Classic wedding car guide", href: "/guides/classic-wedding-car-hire-guide" },
    ],
    ui: EN_UI,
    updatedAt: "2026-08-02",
  },

  // ── /it/noleggio-auto-matrimonio (IT) ────────────────────────────────────
  {
    market: "it",
    pathType: "wedding",
    slug: "noleggio-auto-matrimonio",
    locale: "it",
    localeGroup: "wedding-it",
    h1: "Auto d'epoca con autista per il tuo matrimonio",
    metaTitle: "Noleggio Auto Matrimonio: Auto d'Epoca con Autista | BookAclassic",
    metaDescription:
      "Noleggia un'auto d'epoca per il matrimonio direttamente dal proprietario: Alfa Romeo, Fiat 500, Lancia e berline di rappresentanza con autista, in tutta Italia.",
    heroIntro:
      "Alfa Romeo, Fiat 500, Lancia e grandi berline di rappresentanza: da Milano e Roma fino alla Puglia e alla Sicilia, i proprietari pubblicano le loro auto d'epoca su BookAclassic, quasi sempre con autista, preparate e addobbate per il vostro matrimonio.",
    heroImage: publicAssetUrl("cars/1060304/fb71d6ad-c161-4d02-b60e-6bca1f022e76_1280w.webp"),
    heroAlt: "Citroën Traction Avant per matrimonio in Italia",
    sections: [
      {
        heading: "Perché noleggiare direttamente dal proprietario",
        body:
          "Il noleggio matrimoniale in Italia è dominato dalle agenzie, che rivendono sempre le stesse auto a prezzi da agenzia. Su BookAclassic trattate direttamente con il proprietario (una Giulia Spider, una Fiat 500 color crema, una Lancia Flaminia o una berlina Maserati), che guida personalmente la sua auto, preparata e addobbata. Prenotare direttamente significa disponibilità reale, prezzi onesti e un autista che conosce ogni strada fino alla chiesa. Ogni annuncio mostra il prezzo aggiornato e inviare una richiesta è gratuito.",
      },
      {
        heading: "L'auto giusta per il vostro matrimonio",
        body:
          "Un matrimonio in villa in Toscana rende al meglio con una Spider aperta o una Flaminia; una cerimonia al Sud chiama per tradizione una grande berlina bianca. La Fiat 500 conquista tutti: è abbastanza piccola per i vicoli medievali e irresistibile in ogni album. Valutate il percorso (i borghi in collina premiano le auto compatte), la salita in auto con l'abito, e il tragitto tra cerimonia e ricevimento: i proprietari pianificano volentieri le soste fotografiche nei punti panoramici.",
      },
      {
        heading: "Pacchetti, tempi e quando prenotare",
        body:
          "Un pacchetto tipico copre tre-quattro ore: la presa, il viaggio alla cerimonia, l'attesa, le fotografie e l'arrivo al ricevimento, con nastri e spesso una bottiglia di prosecco su richiesta. Maggio, giugno e settembre si esauriscono per primi. Per un'auto specifica muovetevi con sei-nove mesi di anticipo. Indicate nel primo messaggio l'orario della cerimonia, entrambi gli indirizzi e i vostri piani fotografici.",
      },
    ],
    faqs: [
      {
        q: "Quanto costa noleggiare un'auto d'epoca per il matrimonio?",
        a: "La maggior parte dei pacchetti va da circa 300 a 800 € per tre-quattro ore, a seconda dell'auto. Una Fiat 500 è nella fascia bassa, mentre Spider aperte e grandi berline sono in quella alta. Ogni annuncio mostra il prezzo aggiornato.",
      },
      {
        q: "L'auto è con autista?",
        a: "Quasi sempre. È il proprietario a guidare la propria auto. La giornata resta senza pensieri e al volante c'è chi conosce meglio strada e vettura.",
      },
      {
        q: "Il proprietario addobba l'auto?",
        a: "I nastri nei vostri colori sono quasi sempre inclusi su richiesta; fiori e dettagli personalizzati si concordano nei messaggi di prenotazione.",
      },
      {
        q: "Con quanto anticipo dobbiamo prenotare?",
        a: "Sei-nove mesi per un sabato di alta stagione, soprattutto a maggio, giugno e settembre. Le date infrasettimanali e fuori stagione si trovano anche con poche settimane.",
      },
      {
        q: "I proprietari si spostano anche fuori città?",
        a: "Sì. La maggior parte copre località entro un'ora da casa, più lontano per prenotazioni a giornata intera. Scrivete entrambi gli indirizzi nella richiesta.",
      },
    ],
    listingsQuery: { country_code: "IT", rental_type: "chauffeured" },
    related: [
      { label: "Wedding car hire in Italy (English)", href: "/wedding-car-hire" },
      { label: "Noleggio Chevrolet in Italia", href: "/hire/chevrolet" },
      { label: "La guida completa alle auto per matrimoni", href: "/hire/wedding-car-hire" },
    ],
    ui: IT_UI,
    updatedAt: "2026-08-02",
  },

  // ── /de/wedding-car-hire (EN) ────────────────────────────────────────────
  {
    market: "de",
    canonicalPath: "/de/hochzeitsauto-mieten",
    pathType: "wedding",
    locale: "en",
    localeGroup: "wedding-de",
    h1: "Classic Wedding Car Hire in Germany",
    metaTitle: "Classic Wedding Car Hire Germany: Chauffeur Cars from Local Owners",
    metaDescription:
      "Hire a classic wedding car in Germany direct from its owner: vintage Mercedes, Porsches and American convertibles, chauffeured from Berlin to Munich.",
    heroIntro:
      "Germany's private classic scene is enormous, and much of it is bookable: vintage Mercedes saloons, air-cooled Porsches, American convertibles and pre-war rarities listed by their owners from Hamburg to Munich, most offered chauffeured for weddings.",
    heroImage: publicAssetUrl("cars/1055300/legacy-1212247_1280w.webp"),
    heroAlt: "Rolls-Royce Silver Shadow wedding car listed in Germany",
    sections: [
      {
        heading: "Why book direct from a German owner",
        body:
          "German wedding-car hire has traditionally meant a local firm with one white saloon. On BookAclassic you book the owner's personal car (a Mercedes W111 or Adenauer, a Beetle cabriolet, a '60s Porsche or an American convertible), prepared, ribboned and driven by the owner. German owners are famously meticulous; the cars arrive concours-clean. Without fleet overheads the package price usually beats trade rates, and every listing shows live pricing with free requests.",
      },
      {
        heading: "Matching the car to a German wedding",
        body:
          "A Standesamt-plus-church day suits a stately Mercedes saloon with room for the dress; a summer vineyard wedding on the Rhine or Mosel is made for an open convertible. Beetles and Bullis bring charm to relaxed celebrations, and a Pagoda or 911 makes the couple's escape between ceremony and party unforgettable. Think about dress access, the photographs you want and the distance between venues, then put the details in your first message.",
      },
      {
        heading: "Packages, timing and booking windows",
        body:
          "A typical owner package covers three to four hours: pickup, the ceremony run, waiting time, photographs and the reception arrival, ribbons on request. May to September Saturdays book out months ahead. For a specific car, start six months early. Midweek civil ceremonies are far easier to match and often discounted.",
      },
    ],
    faqs: [
      {
        q: "How much does classic wedding car hire cost in Germany?",
        a: "Most owner packages run roughly €250–€700 for three to four hours depending on the car. Each listing shows the owner's live rate; sending a request is free.",
      },
      {
        q: "Is the car chauffeur-driven?",
        a: "Almost always. The owner drives their own car, keeping the day stress-free with the person who knows the car best at the wheel.",
      },
      {
        q: "Do owners decorate the car?",
        a: "Ribbons in your colours are near-universal on request; flower arrangements can usually be agreed in the booking messages.",
      },
      {
        q: "How far ahead should we book?",
        a: "Six months or more for a peak-season Saturday. Midweek and off-season dates can often be secured within weeks.",
      },
      {
        q: "Do owners cover venues outside the big cities?",
        a: "Yes. Cars are listed across Germany, and most owners cover venues within an hour of home, further for full-day bookings. Include both addresses in your request.",
      },
    ],
    listingsQuery: { country_code: "DE", rental_type: "chauffeured" },
    related: [
      { label: "Hochzeitsauto mieten: auf Deutsch lesen", href: "/hochzeitsauto-mieten" },
      { label: "The complete wedding car guide", href: "/hire/wedding-car-hire" },
      { label: "Classic wedding car guide", href: "/guides/classic-wedding-car-hire-guide" },
    ],
    ui: EN_UI,
    updatedAt: "2026-08-02",
  },

  // ── /de/hochzeitsauto-mieten (DE) ────────────────────────────────────────
  {
    market: "de",
    pathType: "wedding",
    slug: "hochzeitsauto-mieten",
    locale: "de",
    localeGroup: "wedding-de",
    h1: "Oldtimer mit Chauffeur für die Hochzeit mieten",
    metaTitle: "Hochzeitsauto mieten: Oldtimer mit Chauffeur | BookAclassic",
    metaDescription:
      "Mieten Sie einen Oldtimer als Hochzeitsauto direkt vom Besitzer: Mercedes-Klassiker, Porsche und amerikanische Cabrios mit Chauffeur, in ganz Deutschland.",
    heroIntro:
      "Deutschlands private Oldtimerszene ist riesig, und ein großer Teil davon ist buchbar: Mercedes-Limousinen, luftgekühlte Porsche, amerikanische Cabrios und Vorkriegsraritäten, eingestellt von ihren Besitzern von Hamburg bis München, fast immer mit Chauffeur für Hochzeiten.",
    heroImage: publicAssetUrl("cars/1027534/legacy-1016314_1280w.webp"),
    heroAlt: "Cadillac De Ville Cabrio als Hochzeitsauto in Deutschland",
    sections: [
      {
        heading: "Warum direkt vom Besitzer mieten",
        body:
          "Klassische Hochzeitswagen-Vermietung heißt oft: ein lokaler Anbieter, eine weiße Limousine. Auf BookAclassic buchen Sie das persönliche Fahrzeug des Besitzers (einen Mercedes W111 oder Adenauer, ein Käfer-Cabrio, einen 60er-Jahre-Porsche oder ein amerikanisches Cabrio), vorbereitet, geschmückt und vom Besitzer selbst gefahren. Deutsche Besitzer sind bekannt gründlich; die Fahrzeuge kommen im Concours-Zustand. Ohne Flottenkosten liegt der Paketpreis meist unter den Gewerbetarifen, jede Anzeige zeigt den aktuellen Preis und Anfragen sind kostenlos.",
      },
      {
        heading: "Das passende Auto für Ihre Hochzeit",
        body:
          "Für Standesamt und Kirche passt eine repräsentative Mercedes-Limousine mit Platz fürs Kleid; eine Sommerhochzeit im Weinberg an Rhein oder Mosel verlangt nach einem offenen Cabrio. Käfer und Bulli bringen Charme in lockere Feiern, und eine Pagode oder ein 911 macht die Fahrt des Paares zwischen Trauung und Feier unvergesslich. Denken Sie an den Einstieg im Kleid, die gewünschten Fotos und die Strecke zwischen den Orten, und schreiben Sie die Details in die erste Nachricht.",
      },
      {
        heading: "Pakete, Timing und Buchungsfenster",
        body:
          "Ein typisches Paket umfasst drei bis vier Stunden: Abholung, Fahrt zur Trauung, Wartezeit, Fotos und die Ankunft bei der Feier; Bänder gibt es auf Wunsch. Samstage von Mai bis September sind Monate im Voraus vergeben; für ein bestimmtes Auto sollten Sie sechs Monate vorher anfragen. Standesamtliche Trauungen unter der Woche sind deutlich leichter zu besetzen und oft günstiger.",
      },
    ],
    faqs: [
      {
        q: "Was kostet es, ein Hochzeitsauto zu mieten?",
        a: "Die meisten Pakete liegen je nach Fahrzeug bei etwa 250–700 € für drei bis vier Stunden. Jede Anzeige zeigt den aktuellen Preis des Besitzers; Anfragen sind kostenlos.",
      },
      {
        q: "Fährt der Besitzer das Auto?",
        a: "Fast immer. Der Besitzer fährt sein eigenes Fahrzeug. Das hält den Tag entspannt, und am Steuer sitzt die Person, die das Auto am besten kennt.",
      },
      {
        q: "Wird das Auto geschmückt?",
        a: "Bänder in Ihren Farben sind auf Wunsch fast immer dabei; Blumenschmuck lässt sich in den Buchungsnachrichten vereinbaren.",
      },
      {
        q: "Wie früh sollten wir buchen?",
        a: "Mindestens sechs Monate vor einem Samstag in der Hochsaison. Termine unter der Woche und außerhalb der Saison sind oft kurzfristig möglich.",
      },
      {
        q: "Fahren Besitzer auch außerhalb der Großstädte?",
        a: "Ja. Fahrzeuge sind in ganz Deutschland eingestellt. Die meisten Besitzer decken Orte im Umkreis von einer Stunde ab, bei Ganztagesbuchungen auch mehr. Nennen Sie beide Adressen in der Anfrage.",
      },
    ],
    listingsQuery: { country_code: "DE", rental_type: "chauffeured" },
    related: [
      { label: "Wedding car hire in Germany (English)", href: "/wedding-car-hire" },
      { label: "Der große Hochzeitsauto-Ratgeber", href: "/hire/wedding-car-hire" },
    ],
    ui: DE_UI,
    updatedAt: "2026-08-02",
  },

  // ── /ca/wedding-car-hire (EN) ────────────────────────────────────────────
  {
    market: "ca",
    pathType: "wedding",
    locale: "en",
    h1: "Classic Wedding Car Hire in Canada",
    metaTitle: "Classic Wedding Car Rental Canada: Chauffeur Cars from Local Owners",
    metaDescription:
      "Rent a classic wedding car in Canada direct from its owner: vintage convertibles, muscle cars and grand saloons, chauffeured from Toronto to Vancouver.",
    heroIntro:
      "From Ontario to British Columbia, Canadian owners list their privately kept classics on BookAclassic: vintage convertibles, muscle cars and grand saloons, most offered chauffeured for weddings by the owners themselves.",
    heroImage: publicAssetUrl("cars/1024888/legacy-1007838_1280w.webp"),
    heroAlt: "1964 Rolls-Royce Silver Cloud III wedding car listed in Canada",
    sections: [
      {
        heading: "Why book direct from a Canadian owner",
        body:
          "Wedding-car rental in Canada usually means a limo company with one 'vintage' option. On BookAclassic you book the owner's personal car (a '60s convertible, a big-block muscle car, or a stately saloon), prepared, ribboned and driven by the owner. Direct booking means real choice, honest pricing without fleet overheads, and a driver who genuinely cares about the car in your photographs. Every listing shows live pricing and requests are free.",
      },
      {
        heading: "Matching the car to a Canadian wedding",
        body:
          "A summer vineyard wedding in Niagara or the Okanagan is made for an open convertible; a downtown hotel ceremony suits a grand saloon with room for the dress. Muscle cars photograph brilliantly for couples who want something louder. Canadian distances are real. If ceremony and reception are far apart, ask about a full-day booking and plan photo stops together.",
      },
      {
        heading: "Packages, timing and booking windows",
        body:
          "A typical owner package covers three to four hours: pickup, the ceremony run, waiting time, photographs and the reception arrival, ribbons on request. The season is short; June to September Saturdays book out months ahead, so start six months early for a specific car. Off-season and midweek dates are far easier.",
      },
    ],
    faqs: [
      {
        q: "How much does classic wedding car rental cost in Canada?",
        a: "Most owner packages run roughly CA$400–$900 for three to four hours depending on the car. Each listing shows the owner's live rate; sending a request is free.",
      },
      {
        q: "Is the car chauffeur-driven?",
        a: "Almost always. The owner drives their own car, keeping the day stress-free with the person who knows the car best behind the wheel.",
      },
      {
        q: "Do owners decorate the car?",
        a: "Ribbons in your colours are near-universal on request; flowers and personal touches can usually be arranged in the booking messages.",
      },
      {
        q: "How far will owners travel?",
        a: "Most cover venues within an hour of home, further for full-day bookings. Include both addresses in your request and the owner will confirm coverage.",
      },
    ],
    listingsQuery: { country_code: "CA", rental_type: "chauffeured" },
    related: [
      { label: "Chevrolet hire in Canada", href: "/hire/chevrolet" },
      { label: "The complete wedding car guide", href: "/hire/wedding-car-hire" },
      { label: "Classic wedding car guide", href: "/guides/classic-wedding-car-hire-guide" },
    ],
    ui: EN_UI,
    updatedAt: "2026-08-02",
  },

  // ── /au/wedding-car-hire (EN) ────────────────────────────────────────────
  {
    market: "au",
    pathType: "wedding",
    locale: "en",
    h1: "Classic Wedding Car Hire in Australia",
    metaTitle: "Classic Wedding Car Hire Australia: Chauffeur Cars from Local Owners",
    metaDescription:
      "Hire a classic wedding car in Australia direct from its owner: vintage convertibles, classic Holdens and grand saloons, chauffeured from Sydney to Perth.",
    heroIntro:
      "Australian owners list their privately kept classics on BookAclassic from Sydney and Melbourne to Brisbane and Perth: vintage convertibles, classic Holdens and Fords, and grand European saloons, most offered chauffeured for weddings.",
    heroImage: publicAssetUrl("cars/1039887/legacy-1215582_1280w.webp"),
    heroAlt: "1966 Cadillac Eldorado convertible wedding car listed in Australia",
    sections: [
      {
        heading: "Why book direct from an Australian owner",
        body:
          "Australian wedding-car hire is dominated by fleet operators running the same few white saloons every Saturday. On BookAclassic you book the owner's personal car (an FJ Holden, a '60s Mustang convertible, a Jaguar or a grand Mercedes), prepared, ribboned and driven by the owner. Without fleet overheads the package price is usually friendlier, the car is better kept, and the person driving actually loves it. Every listing shows live pricing and requests are free.",
      },
      {
        heading: "Matching the car to an Australian wedding",
        body:
          "A beach or vineyard wedding is made for an open convertible; a city church ceremony suits a stately saloon with room for the dress. Classic Holdens and Falcons bring genuine Australian character to country weddings, and European grand tourers photograph beautifully against Hunter Valley or Yarra vines. Think about heat (convertibles need a top-up plan in January), dress access and the distance between venues.",
      },
      {
        heading: "Packages, timing and booking windows",
        body:
          "A typical owner package covers three to four hours: pickup, the ceremony run, waiting time, photographs and the reception arrival, ribbons on request. Spring and autumn Saturdays, the Australian peak, book out months ahead; start six months early for a specific car. Midweek dates are dramatically easier and often discounted.",
      },
    ],
    faqs: [
      {
        q: "How much does classic wedding car hire cost in Australia?",
        a: "Most owner packages run roughly A$400–$900 for three to four hours depending on the car. Each listing shows the owner's live rate; sending a request is free.",
      },
      {
        q: "Is the car chauffeur-driven?",
        a: "Almost always. The owner drives their own car, keeping the day stress-free with the person who knows the car best behind the wheel.",
      },
      {
        q: "Do owners decorate the car?",
        a: "Ribbons in your colours are near-universal on request; flowers and personal touches can usually be arranged in the booking messages.",
      },
      {
        q: "How far ahead should we book?",
        a: "Six months or more for a spring or autumn Saturday. Winter and midweek dates can often be secured within weeks.",
      },
      {
        q: "How far will owners travel?",
        a: "Most cover venues within an hour of home, further for full-day bookings. Include both addresses in your request and the owner will confirm coverage and any travel supplement.",
      },
    ],
    listingsQuery: { country_code: "AU", rental_type: "chauffeured" },
    related: [
      { label: "The complete wedding car guide", href: "/hire/wedding-car-hire" },
      { label: "Classic wedding car guide", href: "/guides/classic-wedding-car-hire-guide" },
    ],
    ui: EN_UI,
    updatedAt: "2026-08-02",
  },

  // ── /nz/wedding-car-hire (EN) ────────────────────────────────────────────
  {
    market: "nz",
    pathType: "wedding",
    locale: "en",
    h1: "Classic Wedding Car Hire in New Zealand",
    metaTitle: "Classic Wedding Car Hire New Zealand: Chauffeur Cars from Local Owners",
    metaDescription:
      "Hire a classic wedding car in New Zealand direct from its owner: vintage convertibles and grand saloons, chauffeured from Auckland to Otago.",
    heroIntro:
      "New Zealand punches far above its weight in preserved classics, and its owners list them on BookAclassic from Auckland and Wellington to Christchurch and Otago, most offered chauffeured for weddings, driven by the people who keep them.",
    heroImage: publicAssetUrl("cars/1037390/legacy-1076773_1280w.webp"),
    heroAlt: "1973 Citroën DS23 wedding car listed in New Zealand",
    sections: [
      {
        heading: "Why book direct from a Kiwi owner",
        body:
          "New Zealand's dry climate and car-keeping culture mean the classics here are genuinely good. On BookAclassic you book them directly from their owners rather than through a fleet operator. A '50s saloon, a '60s convertible or a grand European tourer arrives prepared and ribboned, driven by the owner. Without fleet overheads the price is friendlier, and the person at the wheel knows every kilometre of the route. Requests are free and every listing shows live pricing.",
      },
      {
        heading: "Matching the car to a New Zealand wedding",
        body:
          "A vineyard wedding in Marlborough or Central Otago is made for an open convertible with the ranges behind; a city church ceremony suits a stately saloon with room for the dress. For rural venues, ask the owner about gravel access early. Some owners prefer sealed roads, others relish the drive. Photo stops are half the point in New Zealand; plan them into the route with the owner.",
      },
      {
        heading: "Packages, timing and booking windows",
        body:
          "A typical owner package covers three to four hours: pickup, the ceremony run, waiting time, photographs and the reception arrival, ribbons on request. The season peaks November to March. Book a specific car six months out for a summer Saturday. Winter and midweek dates are far easier and often discounted.",
      },
    ],
    faqs: [
      {
        q: "How much does classic wedding car hire cost in New Zealand?",
        a: "Most owner packages run roughly NZ$400–$900 for three to four hours depending on the car. Each listing shows the owner's live rate; sending a request is free.",
      },
      {
        q: "Is the car chauffeur-driven?",
        a: "Almost always. The owner drives their own car, keeping the day stress-free with the person who knows the car best behind the wheel.",
      },
      {
        q: "Do owners decorate the car?",
        a: "Ribbons in your colours are near-universal on request; flowers and personal touches can usually be arranged in the booking messages.",
      },
      {
        q: "Our venue is rural. Is that a problem?",
        a: "Usually not, but raise gravel or steep access in your first message. Owners know their cars' limits and will plan the route, or suggest a photogenic meeting point, accordingly.",
      },
    ],
    listingsQuery: { country_code: "NZ", rental_type: "chauffeured" },
    related: [
      { label: "The complete wedding car guide", href: "/hire/wedding-car-hire" },
      { label: "Classic wedding car guide", href: "/guides/classic-wedding-car-hire-guide" },
    ],
    ui: EN_UI,
    updatedAt: "2026-08-02",
  },

  // ── /ie/wedding-car-hire (EN) ────────────────────────────────────────────
  {
    market: "ie",
    pathType: "wedding",
    locale: "en",
    h1: "Classic Wedding Car Hire in Ireland",
    metaTitle: "Classic Wedding Car Hire Ireland: Chauffeur Cars from Local Owners",
    metaDescription:
      "Hire a classic wedding car in Ireland direct from its owner: vintage Rolls-Royces, Jaguars and charming classics, chauffeured from Dublin to Cork and Galway.",
    heroIntro:
      "Irish owners list their privately kept classics on BookAclassic from Dublin and Cork to Galway: stately saloons, vintage Jaguars and characterful classics, most offered chauffeured for weddings by the owners themselves.",
    heroImage: publicAssetUrl("cars/1052777/legacy-1221934_1280w.webp"),
    heroAlt: "1962 Rolls-Royce Silver Cloud II wedding car listed in Ireland",
    sections: [
      {
        heading: "Why book direct from an Irish owner",
        body:
          "Irish wedding-car hire has long meant a small number of livery firms with hard-worked fleets. On BookAclassic you book the owner's personal car (a Rolls-Royce or Daimler saloon, a Jaguar, a Morris Minor or a VW Beetle), prepared, ribboned and driven by the owner. The cars are better kept, the prices honest without fleet overheads, and the driver is the one person who cares most about how the car looks in your album. Requests are free and every listing shows live pricing.",
      },
      {
        heading: "Matching the car to an Irish wedding",
        body:
          "A country-house or castle wedding suits a stately saloon arriving unhurried up the avenue; a city ceremony gives a characterful small classic its moment. Ireland's wedding distances are often short, but the roads are not fast. Plan generous time buffers with the owner, and if the reception is across the county, ask about a full-day booking with photo stops on the way.",
      },
      {
        heading: "Packages, timing and booking windows",
        body:
          "A typical owner package covers three to four hours: pickup, the ceremony run, waiting time, photographs and the reception arrival, ribbons on request. Irish weddings book long. For a peak-season Friday or Saturday, secure the car six to nine months out. Midweek dates, increasingly popular in Ireland, are far easier to match.",
      },
    ],
    faqs: [
      {
        q: "How much does classic wedding car hire cost in Ireland?",
        a: "Most owner packages run roughly €300–€700 for three to four hours depending on the car. Each listing shows the owner's live rate; sending a request is free.",
      },
      {
        q: "Is the car chauffeur-driven?",
        a: "Almost always. The owner drives their own car, keeping the day stress-free with the person who knows the car best behind the wheel.",
      },
      {
        q: "Do owners decorate the car?",
        a: "Ribbons in your colours are near-universal on request; flowers and personal touches can usually be arranged in the booking messages.",
      },
      {
        q: "How far ahead should we book?",
        a: "Six to nine months for a peak-season Friday or Saturday; Irish weddings book early. Midweek and off-season dates can often be secured within weeks.",
      },
      {
        q: "How far will owners travel?",
        a: "Most cover venues within an hour of home, further for full-day bookings. Include both addresses in your request and the owner will confirm coverage.",
      },
    ],
    listingsQuery: { country_code: "IE", rental_type: "chauffeured" },
    related: [
      { label: "The complete wedding car guide", href: "/hire/wedding-car-hire" },
      { label: "Classic wedding car guide", href: "/guides/classic-wedding-car-hire-guide" },
    ],
    ui: EN_UI,
    updatedAt: "2026-08-02",
  },

  // ── /za/wedding-car-hire (EN) ────────────────────────────────────────────
  {
    market: "za",
    pathType: "wedding",
    locale: "en",
    h1: "Classic Wedding Car Hire in South Africa",
    metaTitle: "Classic Wedding Car Hire South Africa: Chauffeur Cars from Local Owners",
    metaDescription:
      "Hire a classic wedding car in South Africa direct from its owner: vintage convertibles and grand saloons, chauffeured in Cape Town, Johannesburg and beyond.",
    heroIntro:
      "South African owners list their privately kept classics on BookAclassic from Cape Town and the Winelands to Johannesburg and Durban: vintage convertibles and grand saloons, most offered chauffeured for weddings by the owners themselves.",
    heroImage: publicAssetUrl("cars/1046981/legacy-1153897_1280w.webp"),
    heroAlt: "1957 Bentley S1 wedding car listed in South Africa",
    sections: [
      {
        heading: "Why book direct from a South African owner",
        body:
          "South African wedding-car hire is usually a fleet operator with one or two 'vintage' options. On BookAclassic you book the owner's personal car (a '60s convertible, a grand Mercedes or Jaguar saloon, or a characterful small classic), prepared, ribboned and driven by the owner. Direct booking means real choice, honest pricing without fleet overheads, and a driver who genuinely cares about the car in your photographs. Requests are free and every listing shows live pricing.",
      },
      {
        heading: "Matching the car to a South African wedding",
        body:
          "A Winelands wedding at Stellenbosch or Franschhoek is made for an open convertible against the mountains; a city or garden ceremony suits a stately saloon with room for the dress. The summer season peaks from October to April. Convertibles need a shade plan for midday heat, and golden-hour photo drives are worth planning into the route with the owner.",
      },
      {
        heading: "Packages, timing and booking windows",
        body:
          "A typical owner package covers three to four hours: pickup, the ceremony run, waiting time, photographs and the reception arrival, ribbons on request. October to April Saturdays book out months ahead. Start six months early for a specific car. Winter and midweek dates are far easier and often discounted.",
      },
    ],
    faqs: [
      {
        q: "How much does classic wedding car hire cost in South Africa?",
        a: "Most owner packages run roughly R4,000–R10,000 for three to four hours depending on the car. Each listing shows the owner's live rate; sending a request is free.",
      },
      {
        q: "Is the car chauffeur-driven?",
        a: "Almost always. The owner drives their own car, keeping the day stress-free with the person who knows the car best behind the wheel.",
      },
      {
        q: "Do owners decorate the car?",
        a: "Ribbons in your colours are near-universal on request; flowers and personal touches can usually be arranged in the booking messages.",
      },
      {
        q: "How far will owners travel?",
        a: "Most cover venues within an hour of home, further for full-day bookings. Include both addresses in your request and the owner will confirm coverage and any travel supplement.",
      },
    ],
    listingsQuery: { country_code: "ZA", rental_type: "chauffeured" },
    related: [
      { label: "The complete wedding car guide", href: "/hire/wedding-car-hire" },
      { label: "Classic wedding car guide", href: "/guides/classic-wedding-car-hire-guide" },
    ],
    ui: EN_UI,
    updatedAt: "2026-08-02",
  },
];

/**
 * Task #7156 — market-relative custom wedding slugs (native-language URLs).
 * Consumed by App.tsx to register SPA routes for each native slug.
 */
export const WEDDING_CUSTOM_SLUGS: string[] = Array.from(
  new Set(
    HIRE_LANDINGS.filter((e) => e.pathType === "wedding" && e.slug).map((e) => e.slug as string),
  ),
);

const byKey = new Map<string, HireLanding>();
for (const e of HIRE_LANDINGS) byKey.set(`${e.market}${hireLandingPath(e)}`, e);

/**
 * Look up a hire landing by URL market + market-relative path
 * (e.g. ("uk", "/hire/ford/mustang")). Returns undefined for
 * (market, path) pairs with no registry entry — callers must 404.
 */
export function getHireLanding(market: string, path: string): HireLanding | undefined {
  return byKey.get(`${market.toLowerCase()}${path.replace(/\/+$/, "")}`);
}

/** True when /hire/{slug} refers to a hire-landing make page in this market. */
export function isHireMakeSlug(market: string, slug: string): boolean {
  return byKey.has(`${market.toLowerCase()}/hire/${slug.toLowerCase()}`);
}
